import { useState, useEffect, useRef, Fragment } from "react";
import { apiFetch, fmt, RESERVE_TYPES, PARTY_ROLES, FINANCIAL_PARTY_ROLES, SERVICE_PROVIDER_ROLES, ROLE_CATEGORY, ENGAGEMENT_STATUSES, canOnClaim, can, getUser } from "../api";
import { StatusPill, SubNav, SectionHeader } from "./ui";
import ClaimFiles from "./ClaimFiles";  // CLAIM-FILES-1: Finder-style claim file manager
import EditCompanyModal from "./EditCompanyModal";
import EditContactModal from "./EditContactModal";
import { ClaimReadinessPanel } from "./ClaimReadiness";
import { DeliveryInstructions } from "./DeliveryInstructions";
import ComposeEmailModal from "./ComposeEmailModal";
import { AddVesselModal } from "./pickers";

function FieldRow({ label, value, mono }) {
  return (<tr>
    <td style={{fontWeight:600,color:"var(--text2)",fontSize:12,width:"42%",padding:"5px 0",borderBottom:"1px solid var(--border)",whiteSpace:"nowrap"}}>{label}</td>
    <td style={{fontSize:12,padding:"5px 4px",borderBottom:"1px solid var(--border)",fontFamily:mono?"JetBrains Mono,monospace":"inherit"}}>{value||"—"}</td>
  </tr>);
}

// Chunk 19A — single-row editor used by GeneralTab edit mode.  `kind` drives
// which input renders; `disabled` greys the row out for fields the current
// user can't edit (e.g. claims_support sees full-only fields read-only).
function EditFieldRow({ label, kind, value, onChange, disabled, options }) {
  const inputStyle = {
    fontSize: 12,
    width: "100%",
    padding: "3px 6px",
    border: "1px solid var(--border)",
    borderRadius: 3,
    background: disabled ? "var(--bg2)" : "var(--bg)",
    color: disabled ? "var(--text3)" : "inherit",
    fontFamily: "inherit",
  };
  let input;
  if (kind === "checkbox") {
    input = (<input type="checkbox" checked={!!value} disabled={disabled}
              onChange={e => onChange(e.target.checked)} />);
  } else if (kind === "date") {
    // value may arrive as ISO string or null; <input type="date"> wants YYYY-MM-DD
    const v = value ? String(value).slice(0, 10) : "";
    input = (<input type="date" value={v} disabled={disabled}
              onChange={e => onChange(e.target.value || null)} style={inputStyle} />);
  } else if (kind === "select") {
    input = (<select value={value || ""} disabled={disabled} onChange={e => onChange(e.target.value || null)} style={inputStyle}>
              <option value="">—</option>
              {(options || []).map(o => {
                const ov = (o && typeof o === "object") ? o.value : o;
                const ol = (o && typeof o === "object") ? o.label : o;
                return <option key={ov} value={ov}>{ol}</option>;
              })}
            </select>);
  } else if (kind === "textarea") {
    input = (<textarea value={value || ""} disabled={disabled} rows={3}
              onChange={e => onChange(e.target.value)}
              style={{...inputStyle, fontFamily: "inherit", resize: "vertical"}} />);
  } else {
    input = (<input type="text" value={value || ""} disabled={disabled}
              onChange={e => onChange(e.target.value)} style={inputStyle} />);
  }
  return (<tr>
    <td style={{fontWeight:600,color:"var(--text2)",fontSize:12,width:"42%",padding:"5px 0",borderBottom:"1px solid var(--border)",whiteSpace:"nowrap",verticalAlign:"top"}}>{label}</td>
    <td style={{fontSize:12,padding:"5px 4px",borderBottom:"1px solid var(--border)"}}>{input}</td>
  </tr>);
}

// Backend-locked: BASIC_EDITABLE_FIELDS ∩ (what we surface in 19A) and the
// full-only delta.  Fields not listed here either need pickers (deferred to
// Chunk 19B — lead_adjuster_id, vessel_id,
// default_rate_schedule_id) or are intentionally not user-editable.  Parties-4
// (2026-04-29) retired the secondary_adjuster_id picker and dropped the column
// (sharepoint_folder_url, currency, claim_number, dates auto-set, etc.).
const BASIC_FIELDS = ["loss_description", "loss_location", "date_of_loss", "date_reported", "claim_type", "internal_notes"];
const FULL_ONLY_FIELDS = ["is_jones_act", "litigation_flag", "is_hipaa_sensitive"];
const CLAIM_STATUSES = ["Open", "On Hold", "Pending Info", "Investigating"];  // non-terminal only; terminal close is its own flow
// CLAIM_TYPES retired (CLAIMTYPE-EXPAND-1) — claim type is now the controlled
// taxonomy from GET /claim-types (claim_type_code); Jones Act is the
// is_jones_act flag, not a type (decision 3).

// Chunk 19B — FK picker row.  Renders a <select> populated from `options`
// (an array of {id, label}).  When `allowNone` is true, an explicit "(none)"
// option is shown that, when picked from a previously-set value, fires
// onChange(-1) — the backend translates the -1 sentinel into SQL NULL for
// vessel_id / default_rate_schedule_id (and historically secondary_adjuster_id).
// lead_adjuster_id is required and must NOT use allowNone.
function EditFkRow({ label, value, onChange, options, disabled, loading, allowNone, noneLabel, placeholder, onAddNew }) {
  const inputStyle = {
    fontSize: 12,
    width: "100%",
    padding: "3px 6px",
    border: "1px solid var(--border)",
    borderRadius: 3,
    background: disabled ? "var(--bg2)" : "var(--bg)",
    color: disabled ? "var(--text3)" : "inherit",
    fontFamily: "inherit",
  };
  const v = (value === null || value === undefined) ? "" : String(value);
  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === "__add__") { if (onAddNew) onAddNew(); return; }
    if (raw === "") {
      // Empty selection.  If allowNone and the value used to be set, send -1
      // (clear sentinel).  If value was already null, this is a no-op.
      if (allowNone) onChange(value !== null && value !== undefined ? -1 : null);
      else onChange(null);
      return;
    }
    onChange(parseInt(raw, 10));
  };
  return (<tr>
    <td style={{fontWeight:600,color:"var(--text2)",fontSize:12,width:"42%",padding:"5px 0",borderBottom:"1px solid var(--border)",whiteSpace:"nowrap",verticalAlign:"top"}}>{label}</td>
    <td style={{fontSize:12,padding:"5px 4px",borderBottom:"1px solid var(--border)"}}>
      {loading ? (
        <span><span className="spinner-dot" /> <span style={{fontSize:12,color:"var(--text3)"}}>Loading…</span></span>
      ) : (
        <select value={v} disabled={disabled} onChange={handleChange} style={inputStyle}>
          {allowNone
            ? <option value="">{noneLabel || "(none)"}</option>
            : (!v && <option value="">{placeholder || "— select —"}</option>)}
          {(options || []).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          {onAddNew && !disabled && <option value="__add__">+ Add new…</option>}
        </select>
      )}
    </td>
  </tr>);
}

// Summary→General (Kuhrt 2026-04-29).  Compact financial header rendered
// at the top of GeneralTab.  Replaces the retired Summary tab — reuses the
// same /reserves payload that powers the Reserves tab so numbers stay in
// lockstep across views.  Hidden when the claim has no reserves on file.
export function FinancialSnapshot({ claimId, refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiFetch(`/claims/${claimId}/reserves`)
      .then(d => {
        const reserves = d?.reserves || [];
        setData({
          totalReserve: reserves.reduce((s, r) => s + parseFloat(r.total_reserve   || 0), 0),
          totalPaid:    reserves.reduce((s, r) => s + parseFloat(r.cum_paid        || 0), 0),
          remaining:    reserves.reduce((s, r) => s + parseFloat(r.current_balance || 0), 0),
          count:        reserves.length,
        });
      })
      .catch(() => setData({ totalReserve: 0, totalPaid: 0, remaining: 0, count: 0 }));
  }, [claimId, refreshKey]);
  if (!data || data.count === 0) return null;
  // CLAIMS-WORKSPACE-FINISH-1: compact operational strip (was three full-width
  // cards). Reused on Overview + Financials header.
  const remColor = data.remaining < 0 ? "var(--red)" : (data.remaining === 0 ? "#d97706" : "var(--green)");
  const item = (label, value, color) => (
    <div style={{ padding: "7px 18px" }}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase", color: "var(--text3)" }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, fontFamily: "JetBrains Mono,monospace", marginTop: 1, color: color || "inherit" }}>{fmt.money(value)}</div>
    </div>
  );
  const div = <div style={{ width: 1, alignSelf: "stretch", background: "var(--border)" }} />;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", marginBottom: 14, border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden", maxWidth: "100%", background: "#fff" }}>
      {item("Total Reserve", data.totalReserve, "var(--navy)")}{div}
      {item("Total Payments", data.totalPaid, "var(--text2)")}{div}
      {item("Remaining", data.remaining, remColor)}
    </div>
  );
}

export function GeneralTab({ claim, vca, onClaimUpdated, embedded, onWorkChanged }) {
  // CLAIMANT-INJURY-DASHBOARD-1 (P4): `embedded` (used on the redesigned Overview)
  // renders ONLY the editable Claim Information — the Overview now owns the
  // financial snapshot / readiness / policy summary cards, and the old raw
  // "VCA / Policy Information" dump is retired.
  const user = getUser();
  const canEditFull = canOnClaim("edit_claim", claim, user);
  const canEditBasic = canEditFull || canOnClaim("edit_claim_basic", claim, user);
  const canAssign = canOnClaim("assign_claim", claim, user);
  const canEditAnything = canEditBasic || canAssign;

  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [addVessel, setAddVessel] = useState(false);   // TPA-MATTER-CREATION-1: vessel add-new
  // Chunk 19B — lookup options for FK pickers.  Loaded lazily when entering
  // edit mode for the first time, then cached for the lifetime of this
  // component instance.
  const [lookups, setLookups] = useState({ users: [], vessels: [], rateSchedules: [], claimTypes: [], loaded: false });

  useEffect(() => {
    if (!editMode || lookups.loaded) return;
    Promise.all([
      apiFetch("/users").catch(() => []),
      apiFetch("/vessels").catch(() => []),
      apiFetch("/rate-schedules").catch(() => []),
      apiFetch("/claim-types").catch(() => []),
    ]).then(([users, vessels, rateSchedules, claimTypes]) => {
      setLookups({
        users: Array.isArray(users) ? users.filter(u => u.is_active) : [],
        vessels: Array.isArray(vessels) ? vessels : [],
        rateSchedules: Array.isArray(rateSchedules) ? rateSchedules : [],
        claimTypes: Array.isArray(claimTypes) ? claimTypes : [],
        loaded: true,
      });
    });
  }, [editMode, lookups.loaded]);

  const startEdit = () => {
    // Snapshot current values into the draft.  Only fields the user can
    // actually edit are mutable in the UI; full-only fields are still shown
    // but disabled when the user only has EDIT_CLAIM_BASIC.
    setDraft({
      claim_status: claim.claim_status,
      claim_type: claim.claim_type,
      claim_type_code: claim.claim_type_code,
      date_of_loss: claim.date_of_loss,
      date_reported: claim.date_reported,
      loss_location: claim.loss_location,
      loss_description: claim.loss_description,
      internal_notes: claim.internal_notes,
      is_jones_act: !!claim.is_jones_act,
      litigation_flag: !!claim.litigation_flag,
      is_hipaa_sensitive: !!claim.is_hipaa_sensitive,
      // Chunk 19B FK fields (Parties-4 retired secondary_adjuster_id)
      lead_adjuster_id: claim.lead_adjuster_id ?? null,
      vessel_id: claim.vessel_id ?? null,
      default_rate_schedule_id: claim.default_rate_schedule_id ?? null,
    });
    setErr(null);
    setEditMode(true);
  };

  const cancelEdit = () => { setEditMode(false); setDraft({}); setErr(null); };

  const setField = (k) => (v) => setDraft(d => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    setErr(null);
    // Build a payload of only-changed fields.  Sending unchanged fields would
    // still hit the backend, but the backend already short-circuits same-value
    // writes in the audit log; we mirror that here to keep the request lean.
    const payload = {};
    Object.keys(draft).forEach(k => {
      const before = claim[k];
      const after = draft[k];
      // Normalize null/empty for string compare
      const a = before === undefined ? null : before;
      const b = after === undefined ? null : after;
      // Date comparison: both as YYYY-MM-DD strings
      if (a instanceof Date || (typeof a === "string" && a.length >= 10 && k.startsWith("date_"))) {
        const aStr = a ? String(a).slice(0,10) : null;
        const bStr = b ? String(b).slice(0,10) : null;
        if (aStr !== bStr) payload[k] = bStr;
      } else if (a !== b) {
        payload[k] = b;
      }
    });
    if (Object.keys(payload).length === 0) {
      setEditMode(false);
      setSaving(false);
      return;
    }
    try {
      await apiFetch(`/claims/${claim.id}`, { method: "PATCH", body: payload });
      // Refetch the claim so the parent component sees fresh data.
      const fresh = await apiFetch(`/claims/${claim.id}`);
      if (onClaimUpdated) onClaimUpdated(fresh);
      setEditMode(false);
      setDraft({});
      onWorkChanged && onWorkChanged();
    } catch (e) {
      // apiFetch throws an Error whose .message is the server detail when
      // present; fallbacks for network errors.
      setErr(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ----- read-only mode (existing layout, preserved) -----
  if (!editMode) {
    const left=[["Claim Number",claim.claim_number,true],["Claim Type",claim.claim_type_label||claim.claim_type||vca?.Claim_Type],["Status",claim.claim_status],["Date of Loss",fmt.date(claim.date_of_loss||vca?.Date_Of_Loss)],["Date Reported",fmt.date(claim.date_reported)],["Date Opened",fmt.date(claim.date_opened)],["Date Closed",fmt.date(claim.date_closed)],["Lead Adjuster",claim.lead_adjuster_name||vca?.Assigned_Adjuster],["Vessel",claim.vessel_name||vca?.Vessel],["Default Rate Schedule",claim.default_rate_schedule_name],["Loss Location",claim.loss_location||vca?.Vessel_Location],["Jones Act",claim.is_jones_act?"Yes":"No"],["Litigation",claim.litigation_flag?"⚠ YES":"No"],["HIPAA Sensitive",claim.is_hipaa_sensitive?"⚠ YES":"No"]];
    // WORKBENCH-INSURED-LIFT (2026-05-11): right-rail uses AtticusNW
    // vocabulary — "Insured" = policy-holding company (sourced from
    // claim_parties via /claims/{id} insured_company_name, fall back to
    // vca?.Insurer transitionally); "Claimant" = injured person.
    const right=[["Insured",claim.insured_company_name||vca?.Insurer],["Policy Number",vca?.Policy_Number,true],["Claimant",claim.insured_name||vca?.Insured],["Broker",vca?.Broker],["Fleet",vca?.Fleet],["VCA Claim ID",claim.vca_claim_id,true],["Last VCA Sync",claim.vca_last_synced?fmt.datetime(claim.vca_last_synced):"Never"]];
    return (<>{!embedded && <><FinancialSnapshot claimId={claim.id}/><ClaimReadinessPanel claimId={claim.id}/></>}<div className={embedded ? undefined : "grid-2"}>
      <div>
        <div className="card mb-12" style={{marginBottom:12}}>
          <div className="card-header" style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <h3>Claim Information</h3>
            {canEditAnything && <button className="btn btn-outline btn-sm" onClick={startEdit}>Edit</button>}
          </div>
          <div className="card-body"><table style={{width:"100%"}}><tbody>{left.map(([l,v,m])=><FieldRow key={l} label={l} value={v} mono={m}/>)}</tbody></table></div>
        </div>
        {claim.internal_notes&&<div className="card"><div className="card-header"><h3>Internal Notes</h3></div><div className="card-body" style={{fontSize:12,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{claim.internal_notes}</div></div>}
      </div>
      {!embedded && (<div className="card"><div className="card-header"><h3>Policy Reference</h3></div><div className="card-body">
        <table style={{width:"100%"}}><tbody>{right.map(([l,v,m])=><FieldRow key={l} label={l} value={v} mono={m}/>)}</tbody></table>
      </div></div>)}
    </div></>);
  }

  // ----- edit mode -----
  // Note: full-only fields are visible-but-disabled when user only has BASIC.
  // Mirrors the backend's tiered gating (see permissions.py BASIC_EDITABLE_FIELDS
  // / FULL_EDITABLE_FIELDS).  Adjuster + vessel + rate-schedule pickers ship
  // in Chunk 19B; for now those rows still display read-only values from the
  // claim header (above the tabs) which is unchanged in edit mode.
  return (<>{!embedded && <><FinancialSnapshot claimId={claim.id}/><ClaimReadinessPanel claimId={claim.id}/></>}<div className={embedded ? undefined : "grid-2"}>
    <div>
      <div className="card" style={{marginBottom:12}}>
        <div className="card-header" style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <h3>Claim Information</h3>
          <div style={{display:"flex",gap:6}}>
            <button className="btn btn-outline btn-sm" onClick={cancelEdit} disabled={saving}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving?"Saving…":"Save"}</button>
          </div>
        </div>
        <div className="card-body">
          {err && <div style={{background:"#7f1d1d33",color:"#fca5a5",padding:"6px 10px",borderRadius:4,fontSize:12,marginBottom:10}}>{err}</div>}
          <table style={{width:"100%"}}><tbody>
            <FieldRow label="Claim Number" value={claim.claim_number} mono />
            <EditFieldRow label="Status" kind="select" options={CLAIM_STATUSES}
              value={draft.claim_status} onChange={setField("claim_status")}
              disabled={!canEditFull} />
            <EditFieldRow label="Claim Type" kind="select"
              options={lookups.claimTypes.map(t => ({ value: t.code, label: t.label }))}
              value={draft.claim_type_code} onChange={setField("claim_type_code")}
              disabled={!canEditBasic} />
            <EditFieldRow label="Date of Loss" kind="date"
              value={draft.date_of_loss} onChange={setField("date_of_loss")}
              disabled={!canEditBasic} />
            <EditFieldRow label="Date Reported" kind="date"
              value={draft.date_reported} onChange={setField("date_reported")}
              disabled={!canEditBasic} />
            <FieldRow label="Date Opened" value={fmt.date(claim.date_opened)} />
            <FieldRow label="Date Closed" value={fmt.date(claim.date_closed)} />
            {/* Lead Adjuster is assigned in the Parties tab → WNW Staff */}
            <EditFkRow label="Vessel"
              value={draft.vessel_id}
              onChange={setField("vessel_id")}
              options={lookups.vessels.map(v => ({id: v.id, label: v.vessel_name}))}
              disabled={!canEditFull}
              loading={!lookups.loaded}
              allowNone={true}
              onAddNew={() => setAddVessel(true)} />
            <EditFkRow label="Default Rate Schedule"
              value={draft.default_rate_schedule_id}
              onChange={setField("default_rate_schedule_id")}
              options={lookups.rateSchedules.map(s => ({id: s.id, label: s.name}))}
              disabled={!canEditFull}
              loading={!lookups.loaded}
              allowNone={true}
              noneLabel="(use claim default)" />
            <EditFieldRow label="Loss Location" kind="text"
              value={draft.loss_location} onChange={setField("loss_location")}
              disabled={!canEditBasic} />
            <EditFieldRow label="Jones Act" kind="checkbox"
              value={draft.is_jones_act} onChange={setField("is_jones_act")}
              disabled={!canEditFull} />
            <EditFieldRow label="Litigation" kind="checkbox"
              value={draft.litigation_flag} onChange={setField("litigation_flag")}
              disabled={!canEditFull} />
            <EditFieldRow label="HIPAA Sensitive" kind="checkbox"
              value={draft.is_hipaa_sensitive} onChange={setField("is_hipaa_sensitive")}
              disabled={!canEditFull} />
          </tbody></table>
          {!canEditFull && canEditBasic && (
            <div style={{marginTop:10,fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>
              Some fields are restricted to adjusters and managers.
            </div>
          )}
        </div>
      </div>
      <div className="card">
        <div className="card-header"><h3>Loss Description &amp; Internal Notes</h3></div>
        <div className="card-body">
          <table style={{width:"100%"}}><tbody>
            <EditFieldRow label="Loss Description" kind="textarea"
              value={draft.loss_description} onChange={setField("loss_description")}
              disabled={!canEditBasic} />
            <EditFieldRow label="Internal Notes" kind="textarea"
              value={draft.internal_notes} onChange={setField("internal_notes")}
              disabled={!canEditBasic} />
          </tbody></table>
        </div>
      </div>
    </div>
    {!embedded && (<div className="card"><div className="card-header"><h3>Policy Reference</h3></div><div className="card-body">
      <table style={{width:"100%"}}><tbody>
        <FieldRow label="Insured" value={claim?.insured_company_name || vca?.Insurer} />
        <FieldRow label="Policy Number" value={vca?.Policy_Number} mono />
      </tbody></table>
    </div></div>)}
    {addVessel && <AddVesselModal onClose={() => setAddVessel(false)}
      onCreated={v => {
        setLookups(prev => ({ ...prev, vessels: [...prev.vessels, v].sort((a, b) => (a.vessel_name || "").localeCompare(b.vessel_name || "")) }));
        setDraft(d => ({ ...d, vessel_id: v.id }));
        setAddVessel(false);
      }} />}
  </div></>);
}

// ----- CLAIM-WS-IA B1: Loss Information (re-homed to the CLAIM, mig 104) -----
// Loss is a claim concept. This card renders on the Claim tab below Claim Information
// (which keeps DOL / Date Reported / Loss Location / Vessel). Extended loss detail only —
// no field is editable in two places. Edits /claims/{id}. Claimant tab now holds Injury only.
const CLAIM_LOSS_FIELDS = [
  { k: "time_of_loss", l: "Time of Loss" },
  { k: "loss_county", l: "Loss County" },
  { k: "loss_state", l: "Loss State / Province" },
  { k: "jurisdiction", l: "Jurisdiction" },
  { k: "vessel_type", l: "Vessel Type" },
  { k: "location_on_vessel", l: "Location on Vessel" },
  { k: "operations_at_incident", l: "Operations at Incident" },
  { k: "loss_type", l: "Loss Type" },
  { k: "line_of_business", l: "Line of Business" },
  { k: "cause_of_loss", l: "Cause of Loss" },
  { k: "cat_code", l: "Cat Code" },
  { k: "gross_loss_value", l: "Gross Loss Value", money: true },
  { k: "actual_cash_value", l: "Actual Cash Value", money: true },
  { k: "replacement_cost", l: "Replacement Cost", money: true },
  { k: "additional_loss_notes", l: "Additional Loss Notes", area: true, full: true },
];
export function ClaimLoss({ claim, onClaimUpdated, onWorkChanged }) {
  const canEdit = canOnClaim("edit_claim", claim, getUser());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const start = () => { const d = {}; CLAIM_LOSS_FIELDS.forEach(f => { d[f.k] = claim[f.k] ?? ""; }); setDraft(d); setMsg(null); setEditing(true); };
  const save = async () => {
    setSaving(true); setMsg(null);
    const payload = {};
    CLAIM_LOSS_FIELDS.forEach(f => {
      const before = claim[f.k] == null ? "" : String(claim[f.k]);
      const after = draft[f.k] == null ? "" : String(draft[f.k]);
      if (before !== after) payload[f.k] = after === "" ? null : (f.money ? parseFloat(after) : after);
    });
    try {
      if (Object.keys(payload).length) { await apiFetch(`/claims/${claim.id}`, { method: "PATCH", body: payload }); onClaimUpdated && onClaimUpdated({ ...claim, ...payload }); onWorkChanged && onWorkChanged(); }
      setEditing(false);
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setSaving(false); }
  };
  const isBlank = (f) => { const v = claim[f.k]; return v == null || v === ""; };
  const populated = CLAIM_LOSS_FIELDS.filter(f => !isBlank(f));
  const emptyCount = CLAIM_LOSS_FIELDS.length - populated.length;
  const shown = editing ? CLAIM_LOSS_FIELDS : populated;
  const showVal = (f) => { const v = claim[f.k]; if (v == null || v === "") return "—"; return f.money ? fmt.money(v) : String(v); };
  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-header" style={{ display: "flex", alignItems: "center" }}>
        <h3 style={{ flex: 1, margin: 0 }}>Loss Information</h3>
        {canEdit && !editing && <button className="btn btn-sm" onClick={start}>Edit</button>}
        {editing && <span><button className="btn btn-sm" onClick={() => setEditing(false)} style={{ marginRight: 6 }}>Cancel</button><button className="btn btn-sm btn-blue" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button></span>}
      </div>
      <div className="card-body">
        {msg && <div className="alert-error" style={{ marginBottom: 10 }}>{msg}</div>}
        {!editing && populated.length === 0
          ? <div className="empty-state" style={{ padding: "20px 12px", fontSize: 12.5, color: "var(--text3)" }}>No loss details captured yet.{canEdit ? " Click Edit to add the loss particulars." : ""}</div>
          : <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "12px 24px" }}>
              {shown.map(f => (
                <div key={f.k} style={f.full ? { gridColumn: "1 / -1" } : undefined}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>{f.l}</div>
                  {editing
                    ? (f.area
                        ? <textarea value={draft[f.k] ?? ""} onChange={e => setDraft({ ...draft, [f.k]: e.target.value })} style={{ width: "100%", fontSize: 13, padding: "4px 6px", minHeight: 50 }} />
                        : <input type={f.money ? "number" : "text"} value={draft[f.k] ?? ""} onChange={e => setDraft({ ...draft, [f.k]: e.target.value })} style={{ width: "100%", fontSize: 13, padding: "3px 6px" }} />)
                    : <div style={{ fontSize: 13 }}>{showVal(f)}</div>}
                </div>
              ))}
            </div>
            {!editing && emptyCount > 0 && <div style={{ marginTop: 12, fontSize: 11.5, color: "var(--text3)" }}>{emptyCount} more loss field{emptyCount > 1 ? "s" : ""} not yet filled{canEdit ? " · click Edit to add" : ""}.</div>}
          </>}
      </div>
    </div>
  );
}

// ----- Parties-2: WNW Staff section -----------------------------------
// Renders following adjusters / claims support / admin support / manager
// oversight on a claim.  Lead Adjuster lives on claims.lead_adjuster_id and
// is shown read-only at the top with a hint about where to edit it (General
// tab).  CRUD via /claims/{id}/staff endpoints (Parties-2 backend).

const STAFF_ROLES = ["Following Adjuster", "Claims Support", "Admin Support", "Manager Oversight"];

function StaffSection({ claimId, claim, canAssign, onWorkChanged }) {
  const [staff, setStaff] = useState([]);
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ user_id: "", role: "Following Adjuster", notes: "" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = () => apiFetch(`/claims/${claimId}/staff`).then(setStaff).catch(() => setStaff([]));
  useEffect(() => {
    load();
    apiFetch("/users").then(rs => setUsers(Array.isArray(rs) ? rs.filter(u => u.is_active) : [])).catch(() => setUsers([]));
  }, [claimId]);

  // Lead adjuster info (read-only) — derived from the claim object
  const [leadEdit, setLeadEdit] = useState(false);
  const [leadDraft, setLeadDraft] = useState(claim?.lead_adjuster_id || "");
  const [leadName, setLeadName] = useState(claim?.lead_adjuster_name || "—");
  const [leadSaving, setLeadSaving] = useState(false);
  const saveLead = async () => {
    setLeadSaving(true);
    try {
      await apiFetch(`/claims/${claimId}`, { method: "PATCH", body: { lead_adjuster_id: parseInt(leadDraft) } });
      const u = users.find(x => String(x.id) === String(leadDraft));
      if (u) setLeadName(u.full_name || u.username);
      setLeadEdit(false);
      onWorkChanged && onWorkChanged();   // header Adjuster pill + Overview read from the claim object
    } catch (e) { alert(e?.message || "Save failed"); }
    setLeadSaving(false);
  };

  const roleColor = r => {
    if (r === "Following Adjuster") return { bg: "#dbeafe", color: "#1e40af" };
    if (r === "Claims Support") return { bg: "#d1fae5", color: "#065f46" };
    if (r === "Admin Support") return { bg: "#fef3c7", color: "#92400e" };
    if (r === "Manager Oversight") return { bg: "#ede9fe", color: "#4c1d95" };
    return { bg: "#f1f5f9", color: "#334155" };
  };

  const save = async () => {
    if (!form.user_id) { alert("Pick a user"); return; }
    setSaving(true);
    try {
      await apiFetch(`/claims/${claimId}/staff`, {
        method: "POST",
        body: { user_id: parseInt(form.user_id), role: form.role, notes: form.notes || null },
      });
      setShowAdd(false);
      setForm({ user_id: "", role: "Following Adjuster", notes: "" });
      load();
      onWorkChanged && onWorkChanged();
    } catch (e) { alert(e?.message || "Save failed"); }
    setSaving(false);
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditForm({ user_id: s.user_id, role: s.role, notes: s.notes || "" });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const payload = {
        user_id: parseInt(editForm.user_id),
        role: editForm.role,
        notes: editForm.notes || null,
      };
      Object.keys(payload).forEach(k => { if (payload[k] === null) delete payload[k]; });
      await apiFetch(`/claims/${claimId}/staff/${editingId}`, { method: "PATCH", body: payload });
      cancelEdit();
      load();
      onWorkChanged && onWorkChanged();
    } catch (e) { alert(e?.message || "Save failed"); }
    setEditSaving(false);
  };

  const doDelete = async (s) => {
    const name = s.full_name || s.username || "this user";
    if (!window.confirm(`Remove ${name} (${s.role}) from this claim?`)) return;
    setDeletingId(s.id);
    try {
      await apiFetch(`/claims/${claimId}/staff/${s.id}`, { method: "DELETE" });
      load();
      onWorkChanged && onWorkChanged();
    } catch (e) { alert(e?.message || "Delete failed"); }
    setDeletingId(null);
  };

  return (<div style={{marginBottom:16}}>
    <SectionHeader title="WNW Staff"
      actions={canAssign && <button className="btn btn-blue btn-sm" onClick={()=>setShowAdd(!showAdd)}>{showAdd?"Cancel":"+ Add Staff"}</button>} />

    {/* Lead row — assignable here (Parties → WNW Staff) */}
    <div className="card" style={{marginBottom:8,padding:"8px 12px",fontSize:12,display:"flex",alignItems:"center",gap:10,background:"#f8fafc"}}>
      <span className="badge" style={{background:"#fee2e2",color:"#991b1b"}}>Lead Adjuster</span>
      {leadEdit ? (<>
        <select value={leadDraft} onChange={e=>setLeadDraft(e.target.value)} style={{fontSize:12,padding:"2px 6px"}}>
          <option value="">— select adjuster —</option>
          {users.map(u=><option key={u.id} value={u.id}>{u.full_name||u.username}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" onClick={saveLead} disabled={leadSaving||!leadDraft} style={{padding:"2px 8px",fontSize:11}}>{leadSaving?"…":"Save"}</button>
        <button className="btn btn-outline btn-sm" onClick={()=>setLeadEdit(false)} style={{padding:"2px 8px",fontSize:11}}>Cancel</button>
      </>) : (<>
        <span style={{fontWeight:600}}>{leadName}</span>
        {canAssign && <button className="btn btn-outline btn-sm" onClick={()=>{setLeadDraft(claim?.lead_adjuster_id||"");setLeadEdit(true);}} style={{marginLeft:"auto",padding:"2px 8px",fontSize:11}}>Edit</button>}
      </>)}
    </div>

    {showAdd && <div className="card mb-12" style={{marginBottom:8}}><div className="card-body">
      <div className="form-grid">
        <div className="form-group"><label>User</label>
          <select value={form.user_id} onChange={e=>setForm({...form,user_id:e.target.value})}>
            <option value="">Select user...</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
          </select>
        </div>
        <div className="form-group"><label>Role</label>
          <select value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
            {STAFF_ROLES.map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div className="form-group" style={{gridColumn:"1 / -1"}}><label>Notes (optional)</label>
          <input value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="e.g. covering for vacation"/>
        </div>
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:8}}>
        <button className="btn btn-outline btn-sm" onClick={()=>setShowAdd(false)}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving?"Saving...":"Add Staff"}</button>
      </div>
    </div></div>}

    <div className="card table-wrap">
      {staff.length === 0
        ? <div className="empty-state" style={{padding:"10px 12px",fontSize:12,color:"var(--text3)"}}>No following staff assigned</div>
        : <table>
            <thead><tr><th>Role</th><th>Name</th><th>Email</th><th>Notes</th><th>Since</th>{canAssign && <th className="text-center">Actions</th>}</tr></thead>
            <tbody>{staff.map(s => {
              const rc = roleColor(s.role);
              if (editingId === s.id) {
                return (<tr key={s.id} style={{background:"#fffbeb"}}>
                  <td><select value={editForm.role} onChange={e=>setEditForm({...editForm,role:e.target.value})} style={{fontSize:11,padding:"2px 4px"}}>{STAFF_ROLES.map(r=><option key={r}>{r}</option>)}</select></td>
                  <td><select value={editForm.user_id} onChange={e=>setEditForm({...editForm,user_id:e.target.value})} style={{fontSize:11,padding:"2px 4px",width:"100%"}}>{users.map(u=><option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}</select></td>
                  <td>{s.email || "—"}</td>
                  <td><input value={editForm.notes} onChange={e=>setEditForm({...editForm,notes:e.target.value})} style={{fontSize:11,padding:"2px 4px",width:"100%"}}/></td>
                  <td className="mono">{fmt.date(s.start_date)}</td>
                  <td className="text-center" style={{whiteSpace:"nowrap"}}>
                    <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={editSaving} style={{padding:"2px 8px",fontSize:11}}>{editSaving?"…":"Save"}</button>{" "}
                    <button className="btn btn-outline btn-sm" onClick={cancelEdit} disabled={editSaving} style={{padding:"2px 8px",fontSize:11}}>Cancel</button>
                  </td>
                </tr>);
              }
              return (<tr key={s.id}>
                <td><span className="badge" style={{background:rc.bg,color:rc.color}}>{s.role}</span></td>
                <td style={{fontWeight:500}}>{s.full_name || s.username || "—"}</td>
                <td className="mono" style={{fontSize:11}}>{s.email || "—"}</td>
                <td style={{fontSize:11,color:"var(--text2)"}}>{s.notes || "—"}</td>
                <td className="mono" style={{fontSize:11}}>{fmt.date(s.start_date)}</td>
                {canAssign && <td className="text-center" style={{whiteSpace:"nowrap"}}>
                  <button className="btn btn-outline btn-sm" onClick={()=>startEdit(s)} disabled={deletingId===s.id || editingId !== null} style={{padding:"2px 8px",fontSize:11}}>Edit</button>{" "}
                  <button className="btn btn-outline btn-sm" onClick={()=>doDelete(s)} disabled={deletingId===s.id || editingId !== null} style={{padding:"2px 8px",fontSize:11,color:"#991b1b",borderColor:"#fecaca"}}>{deletingId===s.id?"…":"Remove"}</button>
                </td>}
              </tr>);
            })}</tbody>
          </table>
      }
    </div>
  </div>);
}

// ----- Parties-1.5: recipient role helpers + contact card --------------
// The 3 booleans (send_*, cc_*, bcc_*) are mutually exclusive in the UI —
// represented as a single dropdown with values None/To/CC/BCC.  Helpers
// translate between the row shape and the UI value.

function recipientValue(row, prefix /* 'invoices' | 'reports' */) {
  if (row[`send_${prefix}`])  return "to";
  if (row[`cc_${prefix}`])    return "cc";
  if (row[`bcc_${prefix}`])   return "bcc";
  return "none";
}

function recipientToBools(prefix, value) {
  // Returns the three booleans set so exactly one is true (or all false for none).
  return {
    [`send_${prefix}`]: value === "to",
    [`cc_${prefix}`]:   value === "cc",
    [`bcc_${prefix}`]:  value === "bcc",
  };
}

const RECIPIENT_OPTIONS = [
  { v: "none", label: "—" },
  { v: "to",   label: "To" },
  { v: "cc",   label: "CC" },
  { v: "bcc",  label: "BCC" },
];

function RecipientPicker({ value, onChange, disabled }) {
  return (<select value={value} onChange={e=>onChange(e.target.value)} disabled={disabled}
    style={{fontSize:11,padding:"2px 4px",width:"100%"}}>
    {RECIPIENT_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
  </select>);
}

function recipientBadge(value) {
  // Read-only display: a small colored pill.
  if (value === "none") return <span style={{color:"var(--text3)"}}>—</span>;
  const colors = {
    to:  { bg: "#dbeafe", color: "#1e40af" },
    cc:  { bg: "#e0e7ff", color: "#3730a3" },
    bcc: { bg: "#f3e8ff", color: "#6b21a8" },
  }[value];
  return <span className="badge" style={{...colors,fontSize:10,padding:"1px 5px"}}>{value.toUpperCase()}</span>;
}

// ----- Parties-3: shared role-color helper used by both Financial and Service sections.
function partyRoleColor(r) {
  if (!r) return { bg: "#f1f5f9", color: "#334155" };
  // Financial roles
  if (r === "Underwriter")          return { bg: "#d1fae5", color: "#065f46" };
  if (r === "Reinsurer")            return { bg: "#a7f3d0", color: "#064e3b" };
  if (r === "Broker")               return { bg: "#fef3c7", color: "#92400e" };
  if (r === "TPA")                  return { bg: "#ede9fe", color: "#4c1d95" };
  if (r === "Vessel Owner")         return { bg: "#dbeafe", color: "#1e40af" };
  if (r === "Coverage Counsel")     return { bg: "#fee2e2", color: "#991b1b" };
  if (r === "Defense Counsel")      return { bg: "#fecaca", color: "#7f1d1d" };
  if (r === "Claimant's Counsel")   return { bg: "#fed7aa", color: "#7c2d12" };
  if (r === "Adjusting Partner")    return { bg: "#bae6fd", color: "#075985" };
  if (r === "Client Other")         return { bg: "#f1f5f9", color: "#334155" };
  // Service roles
  if (r === "Surveyor")             return { bg: "#cffafe", color: "#155e75" };
  if (r === "Repairer")             return { bg: "#fde68a", color: "#854d0e" };
  if (r === "Expert Witness")       return { bg: "#e9d5ff", color: "#581c87" };
  if (r === "Treating Provider")    return { bg: "#bbf7d0", color: "#14532d" };
  if (r === "IME Provider")         return { bg: "#bfdbfe", color: "#1e3a8a" };
  if (r === "Investigator")         return { bg: "#e7e5e4", color: "#44403c" };
  if (r === "Salvor")               return { bg: "#ccfbf1", color: "#134e4a" };
  return { bg: "#f1f5f9", color: "#334155" };
}

const engagementStatusBadge = (s) => {
  if (!s) return <span style={{color:"var(--text3)"}}>—</span>;
  const colors = {
    "Contacted":  { bg: "#e0e7ff", color: "#3730a3" },
    "Engaged":    { bg: "#dbeafe", color: "#1e40af" },
    "Active":     { bg: "#d1fae5", color: "#065f46" },
    "Completed":  { bg: "#f1f5f9", color: "#334155" },
    "Disengaged": { bg: "#fee2e2", color: "#991b1b" },
  }[s] || { bg: "#f1f5f9", color: "#334155" };
  return <span className="badge" style={{...colors,fontSize:10,padding:"1px 5px"}}>{s}</span>;
};

// ----- IA/TPA §13b: TPA representation shares (TPA matters only) -------
// Pick which slip parties THIS TPA job administers; "Recalculate" re-normalizes the
// represented subset's slip % to total 100% (then editable); flags any total ≠ 100%.
// Underwriters' real participation_pct is untouched — this is a second, TPA-only column.
function TpaSlipShares({ claimId, parties, canEdit, reload }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    setRows((parties || []).map(p => ({
      id: p.id,
      label: p.company_name || [p.contact_first_name, p.contact_last_name].filter(Boolean).join(" ") || p.role,
      role: p.role,
      slip_pct: p.participation_pct,
      represented: !!p.tpa_represented,
      tpa_pct: p.tpa_participation_pct ?? "",
    })));
  }, [parties]);
  const setRow = (id, patch) => setRows(rs => rs.map(r => r.id === id ? { ...r, ...patch } : r));
  const recalc = () => {
    const sum = rows.filter(r => r.represented).reduce((s, r) => s + (parseFloat(r.slip_pct) || 0), 0);
    setRows(rs => rs.map(r => {
      if (!r.represented) return { ...r, tpa_pct: "" };
      const base = parseFloat(r.slip_pct) || 0;
      return { ...r, tpa_pct: (sum > 0 ? (base / sum * 100) : 0).toFixed(2) };
    }));
  };
  const anyRep = rows.some(r => r.represented);
  const repTotal = rows.filter(r => r.represented).reduce((s, r) => s + (parseFloat(r.tpa_pct) || 0), 0);
  const off100 = anyRep && Math.abs(repTotal - 100) > 0.01;
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await apiFetch(`/claims/${claimId}/parties/tpa-shares`, { method: "PUT", body: {
        shares: rows.map(r => ({ party_id: r.id, tpa_represented: r.represented,
          tpa_participation_pct: r.represented ? (r.tpa_pct === "" ? null : parseFloat(r.tpa_pct)) : null })) } });
      setMsg("Saved."); reload && reload();
    } catch (e) { setMsg(String(e.message || e).slice(0, 160)); } finally { setBusy(false); }
  };
  if (!parties || !parties.length) return null;
  return (
    <div className="card" style={{ marginBottom: 10, background: "#f8fafc" }}>
      <div style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--navy)" }}>TPA representation</span>
        <span style={{ fontSize: 11, color: "var(--text3)", flex: 1, minWidth: 160 }}>Which slip parties this TPA job administers, re-normalized to 100%.</span>
        {canEdit && <button className="btn btn-sm btn-outline" disabled={busy} onClick={recalc} title="Re-normalize represented shares to 100%">Recalculate</button>}
        {canEdit && <button className="btn btn-sm btn-blue" disabled={busy || off100} onClick={save}>{busy ? "…" : "Save"}</button>}
      </div>
      <table style={{ width: "100%", fontSize: 12 }}><thead><tr>
        <th style={{ textAlign: "left", padding: "4px 12px" }}>Party</th>
        <th className="text-right" style={{ padding: "4px 8px" }} title="Underwriters' slip %">Slip %</th>
        <th className="text-center" style={{ padding: "4px 8px" }}>Represented</th>
        <th className="text-right" style={{ padding: "4px 12px" }}>TPA %</th>
      </tr></thead><tbody>
        {rows.map(r => (<tr key={r.id} style={{ opacity: r.represented ? 1 : 0.5 }}>
          <td style={{ padding: "4px 12px" }}>{r.label} <span style={{ color: "var(--text3)", fontSize: 11 }}>· {r.role}</span></td>
          <td className="text-right mono" style={{ padding: "4px 8px", color: "var(--text3)" }}>{r.slip_pct != null && r.slip_pct !== "" ? Number(r.slip_pct).toFixed(2) : "—"}</td>
          <td className="text-center" style={{ padding: "4px 8px" }}><input type="checkbox" checked={r.represented} disabled={!canEdit} onChange={e => setRow(r.id, { represented: e.target.checked })} /></td>
          <td className="text-right" style={{ padding: "4px 12px" }}>
            {r.represented
              ? <input type="number" value={r.tpa_pct} disabled={!canEdit} onChange={e => setRow(r.id, { tpa_pct: e.target.value })} style={{ width: 72, fontSize: 12, textAlign: "right", padding: "1px 4px" }} min="0" max="100" step="0.01" />
              : <span style={{ color: "var(--text3)" }}>—</span>}
          </td>
        </tr>))}
      </tbody><tfoot><tr>
        <td colSpan={3} style={{ textAlign: "right", padding: "5px 8px", fontSize: 11, fontWeight: 600 }}>Represented total</td>
        <td className="text-right mono" style={{ padding: "5px 12px", fontWeight: 700, color: off100 ? "#b91c1c" : (anyRep ? "#15803d" : "var(--text3)") }}>
          {repTotal.toFixed(2)}%{anyRep ? (off100 ? " ⚠" : " ✓") : ""}
        </td>
      </tr></tfoot></table>
      {off100 && <div style={{ padding: "4px 12px 8px", fontSize: 11, color: "#b91c1c" }}>Represented TPA shares must total exactly 100% before saving.</div>}
      {msg && <div style={{ padding: "4px 12px 8px", fontSize: 11, color: msg === "Saved." ? "#15803d" : "#b91c1c" }}>{msg}</div>}
    </div>
  );
}

// CONTACT-CREATE-1 (2026-07-05): sentinel option value appended to the
// company-scoped contact pickers below. Choosing it opens the shared
// EditContactModal in create mode, locked to the selected company.
const NEW_CONTACT_OPT = "__new_contact__";

// CONTACT-CREATE-1: shared launcher for the create-contact modal, locked to
// the company already chosen in the hosting party form. On save the parent
// flushes the contactsByCompany cache and selects the new contact.
function NewContactModal({ info, companies, refreshCompanyContacts, onPicked, onClose }) {
  const co = companies.find(x => String(x.id) === String(info.companyId));
  return <EditContactModal
    contact={null}
    fixedCompanyId={Number(info.companyId)}
    fixedCompanyName={co ? co.company_name : undefined}
    isAdmin={can("approve_contacts")}
    onClose={onClose}
    onCreated={(row) => { refreshCompanyContacts(info.companyId); onPicked(String(row.id)); }}
  />;
}

// ----- Parties-3: FinancialPartiesSection ------------------------------
// Renders the rows where ROLE_CATEGORY[role] === "financial".  Same shape
// as the pre-Parties-3 Claim Parties section (Policy / Ref / Participation /
// On Slip / Invoices / Reports recipients), plus is_lead and is_assignor
// flags added in Parties-3.
function FinancialPartiesSection({ claimId, parties, companies, canEdit, matterType, ensureContactsLoaded, refreshCompanyContacts, contactsByCompany, contactsLoading, reload, onOpenCompany, onOpenContact }) {
  const [showAdd, setShowAdd] = useState(false);
  const initialFormState = {
    company_id: "", contact_id: "", role: "Underwriter",
    on_the_slip: false, participation_pct: "",
    policy_number: "", reference_number: "",
    invoices: "none", reports: "none",
    is_lead: false, is_assignor: false,
  };
  const [form, setForm] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const formContacts = form.company_id ? (contactsByCompany[form.company_id] || []) : [];
  const editFormContacts = editForm.company_id ? (contactsByCompany[editForm.company_id] || []) : [];

  // CONTACT-CREATE-1: {companyId, target:"add"|"edit"} — set when the user
  // picks "+ New contact…" in a contact select; opens the shared create modal.
  const [newContactFor, setNewContactFor] = useState(null);
  const pickContact = (target) => (e) => {
    const v = e.target.value;
    if (v === NEW_CONTACT_OPT) {
      setNewContactFor({ companyId: target === "add" ? form.company_id : editForm.company_id, target });
      return;
    }
    if (target === "add") setForm(f => ({ ...f, contact_id: v }));
    else setEditForm(f => ({ ...f, contact_id: v }));
  };
  const newContactModal = newContactFor && <NewContactModal
    info={newContactFor} companies={companies} refreshCompanyContacts={refreshCompanyContacts}
    onClose={() => setNewContactFor(null)}
    onPicked={(id) => {
      (newContactFor.target === "add" ? setForm : setEditForm)(f => ({ ...f, contact_id: id }));
      setNewContactFor(null);
    }} />;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        company_id: form.company_id ? parseInt(form.company_id) : null,
        contact_id: form.contact_id ? parseInt(form.contact_id) : null,
        role: form.role,
        on_the_slip: form.on_the_slip,
        participation_pct: form.participation_pct ? parseFloat(form.participation_pct) : null,
        policy_number: form.policy_number || null,
        reference_number: form.reference_number || null,
        is_lead: form.is_lead,
        is_assignor: form.is_assignor,
        ...recipientToBools("invoices", form.invoices),
        ...recipientToBools("reports", form.reports),
      };
      await apiFetch(`/claims/${claimId}/parties`, { method: "POST", body: payload });
      setShowAdd(false);
      setForm(initialFormState);
      reload();
    } catch (e) { alert(e?.message || "Save failed"); }
    setSaving(false);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({
      company_id: p.company_id ?? "",
      contact_id: p.contact_id ?? "",
      role: p.role || "",
      on_the_slip: !!p.on_the_slip,
      participation_pct: p.participation_pct ?? "",
      policy_number: p.policy_number || "",
      reference_number: p.reference_number || "",
      invoices: recipientValue(p, "invoices"),
      reports: recipientValue(p, "reports"),
      is_lead: !!p.is_lead,
      is_assignor: !!p.is_assignor,
    });
    if (p.company_id) ensureContactsLoaded(p.company_id);
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const payload = {
        company_id: editForm.company_id === "" ? -1 : parseInt(editForm.company_id),
        contact_id: editForm.contact_id === "" ? -1 : parseInt(editForm.contact_id),
        role: editForm.role,
        on_the_slip: editForm.on_the_slip,
        participation_pct: editForm.participation_pct === "" ? null : parseFloat(editForm.participation_pct),
        policy_number: editForm.policy_number || null,
        reference_number: editForm.reference_number || null,
        is_lead: editForm.is_lead,
        is_assignor: editForm.is_assignor,
        ...recipientToBools("invoices", editForm.invoices),
        ...recipientToBools("reports", editForm.reports),
      };
      Object.keys(payload).forEach(k => { if (payload[k] === null) delete payload[k]; });
      await apiFetch(`/claims/${claimId}/parties/${editingId}`, { method: "PATCH", body: payload });
      cancelEdit();
      reload();
    } catch (e) { alert(e?.message || "Save failed"); }
    finally { setEditSaving(false); }
  };

  const doDelete = async (p) => {
    const companyName = p.company_name || "this party";
    if (!window.confirm(`Remove ${companyName} (${p.role}) from this claim?\n\nThis is reversible by an admin via audit log review, but the party will no longer appear in the active list.`)) return;
    setDeletingId(p.id);
    try {
      await apiFetch(`/claims/${claimId}/parties/${p.id}`, { method: "DELETE" });
      reload();
    } catch (e) { alert(e?.message || "Delete failed"); }
    finally { setDeletingId(null); }
  };

  return (<div style={{marginBottom:16}}>
    {newContactModal}
    <SectionHeader title="Financial Parties"
      actions={canEdit && <button className="btn btn-blue btn-sm" onClick={()=>setShowAdd(!showAdd)}>{showAdd?"Cancel":"+ Add Party"}</button>} />
    {matterType === "tpa" && <TpaSlipShares claimId={claimId} parties={parties} canEdit={canEdit} reload={reload} />}
    {showAdd && <div className="card mb-12" style={{marginBottom:8}}><div className="card-body">
      <div className="form-grid">
        <div className="form-group"><label>Role</label>
          <select value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
            {FINANCIAL_PARTY_ROLES.map(r=><option key={r}>{r}</option>)}
          </select>
        </div>
        <div className="form-group"><label>Company</label>
          <select value={form.company_id} onChange={e=>{const v=e.target.value;setForm({...form,company_id:v,contact_id:""});if(v)ensureContactsLoaded(v);}}>
            <option value="">Select company...</option>
            {companies.map(c=><option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div className="form-group"><label>Contact{contactsLoading && form.company_id ? " …" : ""}</label>
          <select value={form.contact_id} onChange={pickContact("add")} disabled={!form.company_id}>
            <option value="">{form.company_id ? "(none / general)" : "— pick company first —"}</option>
            {formContacts.map(c => <option key={c.id} value={c.id}>{[c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "(unnamed)"}{c.title ? ` — ${c.title}` : ""}</option>)}
            {form.company_id && <option value={NEW_CONTACT_OPT}>+ New contact…</option>}
          </select>
        </div>
        <div className="form-group"><label>Policy #</label><input value={form.policy_number} onChange={e=>setForm({...form,policy_number:e.target.value})} placeholder="Policy #"/></div>
        <div className="form-group"><label>Reference #</label><input value={form.reference_number} onChange={e=>setForm({...form,reference_number:e.target.value})} placeholder="Ref #"/></div>
        <div className="form-group"><label>Participation %</label><input type="number" value={form.participation_pct} onChange={e=>setForm({...form,participation_pct:e.target.value})} placeholder="0.00" min="0" max="100" step="0.01"/></div>
        <div className="form-group"><label>Invoices</label>
          <RecipientPicker value={form.invoices} onChange={v=>setForm({...form,invoices:v})} />
        </div>
        <div className="form-group"><label>Reports</label>
          <RecipientPicker value={form.reports} onChange={v=>setForm({...form,reports:v})} />
        </div>
      </div>
      <div style={{display:"flex",gap:20,margin:"10px 0",flexWrap:"wrap"}}>
        <label style={{display:"flex",gap:6,alignItems:"center",fontSize:12,cursor:"pointer"}}><input type="checkbox" checked={form.on_the_slip} onChange={e=>setForm({...form,on_the_slip:e.target.checked})}/>On the Slip</label>
        <label style={{display:"flex",gap:6,alignItems:"center",fontSize:12,cursor:"pointer"}}><input type="checkbox" checked={form.is_lead} onChange={e=>setForm({...form,is_lead:e.target.checked})}/>Lead</label>
        <label style={{display:"flex",gap:6,alignItems:"center",fontSize:12,cursor:"pointer"}}><input type="checkbox" checked={form.is_assignor} onChange={e=>setForm({...form,is_assignor:e.target.checked})}/>Assignor</label>
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button className="btn btn-outline btn-sm" onClick={()=>setShowAdd(false)}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving?"Saving...":"Add Party"}</button>
      </div>
    </div></div>}
    <div className="card table-wrap">
      {parties.length===0?<div className="empty-state" style={{padding:"10px 12px",fontSize:12,color:"var(--text3)"}}>No financial parties on this claim</div>
      :<table><thead><tr>
        <th>Role</th><th>Company / Contact</th><th>Policy #</th><th>Ref #</th>
        <th className="text-right" title="Slip participation %">%</th>
        <th className="text-center" title="On the slip">Slip</th>
        <th className="text-center" title="Lead underwriter">Lead</th>
        <th className="text-center" title="Assigned to this claim">Assigned</th>
        <th className="text-center" title="Invoice delivery (To / CC / BCC)">Invoice</th>
        <th className="text-center" title="Report delivery (To / CC / BCC)">Report</th>
        {canEdit && <th className="text-center">Actions</th>}
      </tr></thead>
      <tbody>{parties.map(p=>{
        const rc=partyRoleColor(p.role);
        if (editingId === p.id) {
          return (<tr key={p.id} style={{background:"#fffbeb"}}>
            <td><select value={editForm.role} onChange={e=>setEditForm({...editForm,role:e.target.value})} style={{fontSize:11,padding:"2px 4px"}}>{p.role && !FINANCIAL_PARTY_ROLES.includes(p.role) && <option key={p.role}>{p.role}</option>}{FINANCIAL_PARTY_ROLES.map(r=><option key={r}>{r}</option>)}</select></td>
            <td>
              <select value={editForm.company_id} onChange={e=>{const v=e.target.value;setEditForm({...editForm,company_id:v,contact_id:""});if(v)ensureContactsLoaded(v);}} style={{fontSize:11,padding:"2px 4px",width:"100%",marginBottom:3}}>
                <option value="">(none)</option>
                {p.company_id && !companies.some(c=>String(c.id)===String(p.company_id)) && <option value={p.company_id}>{p.company_name || `Company #${p.company_id}`}</option>}
                {companies.map(c=><option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
              <select value={editForm.contact_id} onChange={pickContact("edit")} disabled={!editForm.company_id} style={{fontSize:11,padding:"2px 4px",width:"100%"}}>
                <option value="">(no specific contact)</option>
                {editFormContacts.map(c => <option key={c.id} value={c.id}>{[c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "(unnamed)"}</option>)}
                {editForm.company_id && <option value={NEW_CONTACT_OPT}>+ New contact…</option>}
              </select>
            </td>
            <td><input value={editForm.policy_number} onChange={e=>setEditForm({...editForm,policy_number:e.target.value})} style={{fontSize:11,padding:"2px 4px",width:"100%"}}/></td>
            <td><input value={editForm.reference_number} onChange={e=>setEditForm({...editForm,reference_number:e.target.value})} style={{fontSize:11,padding:"2px 4px",width:"100%"}}/></td>
            <td className="text-right"><input type="number" value={editForm.participation_pct} onChange={e=>setEditForm({...editForm,participation_pct:e.target.value})} style={{fontSize:11,padding:"2px 4px",width:55,textAlign:"right"}} min="0" max="100" step="0.01"/></td>
            <td className="text-center"><input type="checkbox" checked={editForm.on_the_slip} onChange={e=>setEditForm({...editForm,on_the_slip:e.target.checked})}/></td>
            <td className="text-center"><input type="checkbox" checked={editForm.is_lead} onChange={e=>setEditForm({...editForm,is_lead:e.target.checked})}/></td>
            <td className="text-center"><input type="checkbox" checked={editForm.is_assignor} onChange={e=>setEditForm({...editForm,is_assignor:e.target.checked})}/></td>
            <td><RecipientPicker value={editForm.invoices} onChange={v=>setEditForm({...editForm,invoices:v})} /></td>
            <td><RecipientPicker value={editForm.reports} onChange={v=>setEditForm({...editForm,reports:v})} /></td>
            <td className="text-center" style={{whiteSpace:"nowrap"}}>
              <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={editSaving} style={{padding:"2px 8px",fontSize:11}}>{editSaving?"…":"Save"}</button>{" "}
              <button className="btn btn-outline btn-sm" onClick={cancelEdit} disabled={editSaving} style={{padding:"2px 8px",fontSize:11}}>Cancel</button>
            </td>
          </tr>);
        }
        const contactName = [p.contact_first_name, p.contact_last_name].filter(Boolean).join(" ");
        return (<tr key={p.id}>
          <td>
            <span className="badge" style={{background:rc.bg,color:rc.color}}>{p.role}</span>
            {p.is_lead && <span className="badge" style={{background:"#fee2e2",color:"#991b1b",fontSize:9,padding:"1px 4px",marginLeft:4}}>LEAD</span>}
          </td>
          <td>
            <div style={{fontWeight:500}}>{p.company_id && onOpenCompany ? <span onClick={()=>onOpenCompany(p.company_id)} style={{color:"var(--blue,#2E75B6)",cursor:"pointer"}} title="Open company">{p.company_name||"—"}</span> : (p.company_name||"—")}</div>
            {contactName && <div style={{fontSize:11,color:"var(--text2)",marginTop:2}}>{p.contact_id && onOpenContact ? <span onClick={()=>onOpenContact(p.contact_id)} style={{color:"var(--blue,#2E75B6)",cursor:"pointer"}} title="Open contact">{contactName}</span> : contactName}{p.contact_title ? <span style={{color:"var(--text3)"}}>, {p.contact_title}</span> : ""}</div>}
            {(p.contact_email || p.contact_phone) && <div style={{fontSize:11,color:"var(--text3)",marginTop:1,fontFamily:"JetBrains Mono,monospace"}}>
              {p.contact_email || ""}{p.contact_email && p.contact_phone ? " · " : ""}{p.contact_phone || ""}
            </div>}
          </td>
          <td className="mono">{p.policy_number||"—"}</td><td className="mono">{p.reference_number||"—"}</td>
          <td className="text-right">{p.participation_pct?`${p.participation_pct}%`:"—"}</td>
          <td className="text-center">{p.on_the_slip?"✓":""}</td>
          <td className="text-center">{p.is_lead?"✓":""}</td>
          <td className="text-center">{p.is_assignor?"✓":""}</td>
          <td className="text-center">{recipientBadge(recipientValue(p, "invoices"))}</td>
          <td className="text-center">{recipientBadge(recipientValue(p, "reports"))}</td>
          {canEdit && <td className="text-center" style={{whiteSpace:"nowrap"}}>
            <button className="btn btn-outline btn-sm" onClick={()=>startEdit(p)} style={{padding:"2px 8px",fontSize:11}} disabled={deletingId===p.id || editingId !== null}>Edit</button>{" "}
            <button className="btn btn-outline btn-sm" onClick={()=>doDelete(p)} disabled={deletingId===p.id || editingId !== null} style={{padding:"2px 8px",fontSize:11,color:"#991b1b",borderColor:"#fecaca"}}>{deletingId===p.id?"…":"Remove"}</button>
          </td>}
        </tr>);
      })}</tbody></table>}
    </div>
  </div>);
}

// ----- Parties-3: ServiceProvidersSection -------------------------------
// Renders rows where ROLE_CATEGORY[role] === "service".  Different column
// shape than Financial Parties: surfaces engagement_status, engagement_date,
// estimated_cost, report_received instead of policy/participation/recipient.
function ServiceProvidersSection({ claimId, parties, companies, canEdit, ensureContactsLoaded, refreshCompanyContacts, contactsByCompany, contactsLoading, reload, onOpenCompany, onOpenContact }) {
  const [showAdd, setShowAdd] = useState(false);
  const initialFormState = {
    company_id: "", contact_id: "", role: "Surveyor",
    engagement_date: "", engagement_scope_text: "",
    engagement_status: "Contacted",
    billing_terms_text: "", estimated_cost: "",
    report_received: false,
    notes: "",
  };
  const [form, setForm] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const formContacts = form.company_id ? (contactsByCompany[form.company_id] || []) : [];
  const editFormContacts = editForm.company_id ? (contactsByCompany[editForm.company_id] || []) : [];

  // CONTACT-CREATE-1: {companyId, target:"add"|"edit"} — set when the user
  // picks "+ New contact…" in a contact select; opens the shared create modal.
  const [newContactFor, setNewContactFor] = useState(null);
  const pickContact = (target) => (e) => {
    const v = e.target.value;
    if (v === NEW_CONTACT_OPT) {
      setNewContactFor({ companyId: target === "add" ? form.company_id : editForm.company_id, target });
      return;
    }
    if (target === "add") setForm(f => ({ ...f, contact_id: v }));
    else setEditForm(f => ({ ...f, contact_id: v }));
  };
  const newContactModal = newContactFor && <NewContactModal
    info={newContactFor} companies={companies} refreshCompanyContacts={refreshCompanyContacts}
    onClose={() => setNewContactFor(null)}
    onPicked={(id) => {
      (newContactFor.target === "add" ? setForm : setEditForm)(f => ({ ...f, contact_id: id }));
      setNewContactFor(null);
    }} />;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        company_id: form.company_id ? parseInt(form.company_id) : null,
        contact_id: form.contact_id ? parseInt(form.contact_id) : null,
        role: form.role,
        engagement_date: form.engagement_date || null,
        engagement_scope_text: form.engagement_scope_text || null,
        engagement_status: form.engagement_status || null,
        billing_terms_text: form.billing_terms_text || null,
        estimated_cost: form.estimated_cost ? parseFloat(form.estimated_cost) : null,
        report_received: form.report_received,
        notes: form.notes || null,
      };
      await apiFetch(`/claims/${claimId}/parties`, { method: "POST", body: payload });
      setShowAdd(false);
      setForm(initialFormState);
      reload();
    } catch (e) { alert(e?.message || "Save failed"); }
    setSaving(false);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({
      company_id: p.company_id ?? "",
      contact_id: p.contact_id ?? "",
      role: p.role || "",
      engagement_date: p.engagement_date ? String(p.engagement_date).slice(0,10) : "",
      engagement_scope_text: p.engagement_scope_text || "",
      engagement_status: p.engagement_status || "Contacted",
      billing_terms_text: p.billing_terms_text || "",
      estimated_cost: p.estimated_cost ?? "",
      report_received: !!p.report_received,
      notes: p.notes || "",
    });
    if (p.company_id) ensureContactsLoaded(p.company_id);
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const payload = {
        company_id: editForm.company_id === "" ? -1 : parseInt(editForm.company_id),
        contact_id: editForm.contact_id === "" ? -1 : parseInt(editForm.contact_id),
        role: editForm.role,
        engagement_date: editForm.engagement_date || null,
        engagement_scope_text: editForm.engagement_scope_text || null,
        engagement_status: editForm.engagement_status || null,
        billing_terms_text: editForm.billing_terms_text || null,
        estimated_cost: editForm.estimated_cost === "" ? null : parseFloat(editForm.estimated_cost),
        report_received: editForm.report_received,
        notes: editForm.notes || null,
      };
      Object.keys(payload).forEach(k => { if (payload[k] === null) delete payload[k]; });
      await apiFetch(`/claims/${claimId}/parties/${editingId}`, { method: "PATCH", body: payload });
      cancelEdit();
      reload();
    } catch (e) { alert(e?.message || "Save failed"); }
    finally { setEditSaving(false); }
  };

  const doDelete = async (p) => {
    const companyName = p.company_name || "this provider";
    if (!window.confirm(`Remove ${companyName} (${p.role}) from this claim?\n\nThis is reversible by an admin via audit log review, but the row will no longer appear in the active list.`)) return;
    setDeletingId(p.id);
    try {
      await apiFetch(`/claims/${claimId}/parties/${p.id}`, { method: "DELETE" });
      reload();
    } catch (e) { alert(e?.message || "Delete failed"); }
    finally { setDeletingId(null); }
  };

  return (<div style={{marginBottom:16}}>
    {newContactModal}
    <SectionHeader title="Service Providers"
      actions={canEdit && <button className="btn btn-blue btn-sm" onClick={()=>setShowAdd(!showAdd)}>{showAdd?"Cancel":"+ Add Provider"}</button>} />
    {showAdd && <div className="card mb-12" style={{marginBottom:8}}><div className="card-body">
      <div className="form-grid">
        <div className="form-group"><label>Role</label>
          <select value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
            {SERVICE_PROVIDER_ROLES.map(r=><option key={r}>{r}</option>)}
          </select>
        </div>
        <div className="form-group"><label>Status</label>
          <select value={form.engagement_status} onChange={e=>setForm({...form,engagement_status:e.target.value})}>
            {ENGAGEMENT_STATUSES.map(s=><option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group"><label>Company</label>
          <select value={form.company_id} onChange={e=>{const v=e.target.value;setForm({...form,company_id:v,contact_id:""});if(v)ensureContactsLoaded(v);}}>
            <option value="">Select company...</option>
            {companies.map(c=><option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div className="form-group"><label>Contact{contactsLoading && form.company_id ? " …" : ""}</label>
          <select value={form.contact_id} onChange={pickContact("add")} disabled={!form.company_id}>
            <option value="">{form.company_id ? "(none / general)" : "— pick company first —"}</option>
            {formContacts.map(c => <option key={c.id} value={c.id}>{[c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "(unnamed)"}{c.title ? ` — ${c.title}` : ""}</option>)}
            {form.company_id && <option value={NEW_CONTACT_OPT}>+ New contact…</option>}
          </select>
        </div>
        <div className="form-group"><label>Engagement Date</label>
          <input type="date" value={form.engagement_date} onChange={e=>setForm({...form,engagement_date:e.target.value})}/>
        </div>
        <div className="form-group"><label>Estimated Cost ($)</label>
          <input type="number" value={form.estimated_cost} onChange={e=>setForm({...form,estimated_cost:e.target.value})} placeholder="0.00" min="0" step="0.01"/>
        </div>
        <div className="form-group" style={{gridColumn:"1 / -1"}}><label>Engagement Scope</label>
          <input value={form.engagement_scope_text} onChange={e=>setForm({...form,engagement_scope_text:e.target.value})} placeholder="e.g. damage survey, repair estimate, IME on shoulder injury"/>
        </div>
        <div className="form-group" style={{gridColumn:"1 / -1"}}><label>Billing Terms</label>
          <input value={form.billing_terms_text} onChange={e=>setForm({...form,billing_terms_text:e.target.value})} placeholder="e.g. $250/hr + expenses, flat $1500 for report"/>
        </div>
        <div className="form-group" style={{gridColumn:"1 / -1"}}><label>Notes</label>
          <input value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/>
        </div>
      </div>
      <div style={{display:"flex",gap:20,margin:"10px 0"}}>
        <label style={{display:"flex",gap:6,alignItems:"center",fontSize:12,cursor:"pointer"}}><input type="checkbox" checked={form.report_received} onChange={e=>setForm({...form,report_received:e.target.checked})}/>Report Received</label>
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button className="btn btn-outline btn-sm" onClick={()=>setShowAdd(false)}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving?"Saving...":"Add Provider"}</button>
      </div>
    </div></div>}
    <div className="card table-wrap">
      {parties.length===0?<div className="empty-state" style={{padding:"10px 12px",fontSize:12,color:"var(--text3)"}}>No service providers engaged on this claim</div>
      :<table><thead><tr>
        <th>Role</th><th>Company / Contact</th><th>Status</th>
        <th>Engaged</th><th>Scope</th>
        <th className="text-right">Est. Cost</th>
        <th className="text-center">Report</th>
        {canEdit && <th className="text-center">Actions</th>}
      </tr></thead>
      <tbody>{parties.map(p=>{
        const rc=partyRoleColor(p.role);
        if (editingId === p.id) {
          return (<tr key={p.id} style={{background:"#fffbeb"}}>
            <td><select value={editForm.role} onChange={e=>setEditForm({...editForm,role:e.target.value})} style={{fontSize:11,padding:"2px 4px"}}>{p.role && !SERVICE_PROVIDER_ROLES.includes(p.role) && <option key={p.role}>{p.role}</option>}{SERVICE_PROVIDER_ROLES.map(r=><option key={r}>{r}</option>)}</select></td>
            <td>
              <select value={editForm.company_id} onChange={e=>{const v=e.target.value;setEditForm({...editForm,company_id:v,contact_id:""});if(v)ensureContactsLoaded(v);}} style={{fontSize:11,padding:"2px 4px",width:"100%",marginBottom:3}}>
                <option value="">(none)</option>
                {p.company_id && !companies.some(c=>String(c.id)===String(p.company_id)) && <option value={p.company_id}>{p.company_name || `Company #${p.company_id}`}</option>}
                {companies.map(c=><option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
              <select value={editForm.contact_id} onChange={pickContact("edit")} disabled={!editForm.company_id} style={{fontSize:11,padding:"2px 4px",width:"100%"}}>
                <option value="">(no specific contact)</option>
                {editFormContacts.map(c => <option key={c.id} value={c.id}>{[c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "(unnamed)"}</option>)}
                {editForm.company_id && <option value={NEW_CONTACT_OPT}>+ New contact…</option>}
              </select>
            </td>
            <td><select value={editForm.engagement_status} onChange={e=>setEditForm({...editForm,engagement_status:e.target.value})} style={{fontSize:11,padding:"2px 4px"}}>{ENGAGEMENT_STATUSES.map(s=><option key={s}>{s}</option>)}</select></td>
            <td><input type="date" value={editForm.engagement_date} onChange={e=>setEditForm({...editForm,engagement_date:e.target.value})} style={{fontSize:11,padding:"2px 4px"}}/></td>
            <td><input value={editForm.engagement_scope_text} onChange={e=>setEditForm({...editForm,engagement_scope_text:e.target.value})} style={{fontSize:11,padding:"2px 4px",width:"100%"}}/></td>
            <td className="text-right"><input type="number" value={editForm.estimated_cost} onChange={e=>setEditForm({...editForm,estimated_cost:e.target.value})} style={{fontSize:11,padding:"2px 4px",width:80,textAlign:"right"}} min="0" step="0.01"/></td>
            <td className="text-center"><input type="checkbox" checked={editForm.report_received} onChange={e=>setEditForm({...editForm,report_received:e.target.checked})}/></td>
            <td className="text-center" style={{whiteSpace:"nowrap"}}>
              <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={editSaving} style={{padding:"2px 8px",fontSize:11}}>{editSaving?"…":"Save"}</button>{" "}
              <button className="btn btn-outline btn-sm" onClick={cancelEdit} disabled={editSaving} style={{padding:"2px 8px",fontSize:11}}>Cancel</button>
            </td>
          </tr>);
        }
        const contactName = [p.contact_first_name, p.contact_last_name].filter(Boolean).join(" ");
        return (<tr key={p.id}>
          <td><span className="badge" style={{background:rc.bg,color:rc.color}}>{p.role}</span></td>
          <td>
            <div style={{fontWeight:500}}>{p.company_id && onOpenCompany ? <span onClick={()=>onOpenCompany(p.company_id)} style={{color:"var(--blue,#2E75B6)",cursor:"pointer"}} title="Open company">{p.company_name||"—"}</span> : (p.company_name||"—")}</div>
            {contactName && <div style={{fontSize:11,color:"var(--text2)",marginTop:2}}>{p.contact_id && onOpenContact ? <span onClick={()=>onOpenContact(p.contact_id)} style={{color:"var(--blue,#2E75B6)",cursor:"pointer"}} title="Open contact">{contactName}</span> : contactName}{p.contact_title ? <span style={{color:"var(--text3)"}}>, {p.contact_title}</span> : ""}</div>}
            {(p.contact_email || p.contact_phone) && <div style={{fontSize:11,color:"var(--text3)",marginTop:1,fontFamily:"JetBrains Mono,monospace"}}>
              {p.contact_email || ""}{p.contact_email && p.contact_phone ? " · " : ""}{p.contact_phone || ""}
            </div>}
          </td>
          <td>{engagementStatusBadge(p.engagement_status)}</td>
          <td className="mono" style={{fontSize:11}}>{fmt.date(p.engagement_date)}</td>
          <td style={{fontSize:11,color:"var(--text2)"}}>{p.engagement_scope_text || "—"}</td>
          <td className="text-right">{p.estimated_cost ? fmt.money(p.estimated_cost) : "—"}</td>
          <td className="text-center">{p.report_received?"✓":""}</td>
          {canEdit && <td className="text-center" style={{whiteSpace:"nowrap"}}>
            <button className="btn btn-outline btn-sm" onClick={()=>startEdit(p)} style={{padding:"2px 8px",fontSize:11}} disabled={deletingId===p.id || editingId !== null}>Edit</button>{" "}
            <button className="btn btn-outline btn-sm" onClick={()=>doDelete(p)} disabled={deletingId===p.id || editingId !== null} style={{padding:"2px 8px",fontSize:11,color:"#991b1b",borderColor:"#fecaca"}}>{deletingId===p.id?"…":"Remove"}</button>
          </td>}
        </tr>);
      })}</tbody></table>}
    </div>
  </div>);
}

export function PartiesTab({ claimId, claim, workRefresh, onWorkChanged }) {
  const user = getUser();
  const canEdit = claim ? (canOnClaim("edit_claim", claim, user) || canOnClaim("edit_claim_basic", claim, user)) : false;
  const canAssign = claim ? canOnClaim("assign_claim", claim, user) : false;
  const [parties, setParties] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contactsByCompany, setContactsByCompany] = useState({});
  const [contactsLoading, setContactsLoading] = useState(false);
  // LD-DESIGN-012 pattern 1: sub-tab lenses over the parties collection,
  // never stacked sibling sections. Financial Parties is the default lens.
  const [subtab, setSubtab] = useState("financial");

  // Pop-out company/contact modal from a party row (no page navigation).
  const [openEntity, setOpenEntity] = useState(null); // {type, data, schedules?}
  const openCompany = async (id) => {
    try { const [co, sch] = await Promise.all([apiFetch(`/companies/${id}`), apiFetch(`/rate-schedules`)]); setOpenEntity({ type: "company", data: co, schedules: sch }); } catch {}
  };
  const openContact = async (id) => {
    try { setOpenEntity({ type: "contact", data: await apiFetch(`/contacts/${id}`) }); } catch {}
  };

  const load = () => apiFetch(`/claims/${claimId}/parties`).then(setParties).catch(() => {});
  // Stale-data fix (2026-07-03): parties re-fetch when the global workRefresh
  // counter bumps (a sibling surface mutated), not just on claim change — the
  // lenses below render from THIS parent-held collection.
  useEffect(() => { load(); }, [claimId, workRefresh]);
  useEffect(() => {
    apiFetch("/companies?limit=5000").then(setCompanies).catch(() => {});
  }, [claimId]);
  // Every party mutation reloads locally AND bumps the workspace counter so the
  // claim header, financial ribbon, Overview tiles and sibling lenses re-sync.
  const changed = () => { load(); onWorkChanged && onWorkChanged(); };

  const ensureContactsLoaded = (companyId) => {
    if (!companyId || contactsByCompany[companyId]) return;
    setContactsLoading(true);
    apiFetch(`/companies/${companyId}/contacts?picker_mode=true`)
      .then(rs => setContactsByCompany(prev => ({ ...prev, [companyId]: Array.isArray(rs) ? rs : [] })))
      .catch(() => setContactsByCompany(prev => ({ ...prev, [companyId]: [] })))
      .finally(() => setContactsLoading(false));
  };

  // CONTACT-CREATE-1: force-refetch one company's picker cache after an
  // inline contact create ("+ New contact…") so the new row is selectable.
  const refreshCompanyContacts = (companyId) => {
    if (!companyId) return;
    setContactsLoading(true);
    apiFetch(`/companies/${companyId}/contacts?picker_mode=true`)
      .then(rs => setContactsByCompany(prev => ({ ...prev, [companyId]: Array.isArray(rs) ? rs : [] })))
      .catch(() => setContactsByCompany(prev => { const next = { ...prev }; delete next[companyId]; return next; }))
      .finally(() => setContactsLoading(false));
  };

  // Parties-3: split rows into Financial Parties vs Service Providers vs unknown.
  // Unknown roles (e.g., legacy data with a role we don't know yet) default to financial
  // so they remain visible — no row goes missing because of an unknown role.
  // CLAIM-WS-IA B4 (2026-06-24): cluster financial parties by role bucket so big multi-
  // underwriter files scan cleanly — Insured → Rep → Lead UW → Underwriters (by %) → other.
  const _finOrder = { "Insured": 0, "Rep": 1, "Lead Underwriter": 2, "Underwriter": 3, "Broker": 4 };
  const financial = parties.filter(p => ROLE_CATEGORY[p.role] !== "service")
    .sort((a, b) => ((_finOrder[a.role] ?? 9) - (_finOrder[b.role] ?? 9)) || (parseFloat(b.participation_pct || 0) - parseFloat(a.participation_pct || 0)));
  const service   = parties.filter(p => ROLE_CATEGORY[p.role] === "service");

  // Live counts on lens labels once the parties collection has loaded.
  const cnt = (n) => parties.length ? ` (${n})` : "";

  return (<div>
    <SubNav value={subtab} onChange={setSubtab} options={[
      ["financial", `Financial Parties${cnt(financial.length)}`],
      ["staff", "WNW Staff"],
      ["service", `Service Providers${cnt(service.length)}`],
      ["delivery", "Delivery Instructions"],
    ]} />
    {subtab==="financial" && <FinancialPartiesSection
      claimId={claimId} parties={financial} companies={companies} matterType={claim?.matter_type}
      canEdit={canEdit} ensureContactsLoaded={ensureContactsLoaded} refreshCompanyContacts={refreshCompanyContacts}
      contactsByCompany={contactsByCompany} contactsLoading={contactsLoading}
      reload={changed} onOpenCompany={openCompany} onOpenContact={openContact}
    />}
    {subtab==="staff" && <StaffSection claimId={claimId} claim={claim} canAssign={canAssign} onWorkChanged={onWorkChanged} />}
    {subtab==="service" && <ServiceProvidersSection
      claimId={claimId} parties={service} companies={companies}
      canEdit={canEdit} ensureContactsLoaded={ensureContactsLoaded} refreshCompanyContacts={refreshCompanyContacts}
      contactsByCompany={contactsByCompany} contactsLoading={contactsLoading}
      reload={changed} onOpenCompany={openCompany} onOpenContact={openContact}
    />}
    {subtab==="delivery" && <DeliveryInstructions claimId={claimId} canEdit={canEdit} workRefresh={workRefresh} onWorkChanged={onWorkChanged} />}
    {openEntity?.type === "company" && <EditCompanyModal
      company={openEntity.data} schedules={openEntity.schedules}
      isAdmin={can("approve_companies")}
      onClose={() => setOpenEntity(null)}
      onSaved={() => { setOpenEntity(null); setContactsByCompany({}); changed(); }} />}
    {openEntity?.type === "contact" && <EditContactModal
      contact={openEntity.data} isAdmin={can("approve_contacts")}
      onClose={() => setOpenEntity(null)}
      onSaved={() => { setOpenEntity(null); setContactsByCompany({}); changed(); }} />}
  </div>);
}

export function ReservesTab({ claimId, workRefresh, onWorkChanged }) {
  const [data,setData]=useState({reserves:[],history:[]});const [showForm,setShowForm]=useState(false);
  const [form,setForm]=useState({reserve_type:"Medical",current_balance:"",change_note:""});const [saving,setSaving]=useState(false);
  const [closingId,setClosingId]=useState(null);const [err,setErr]=useState("");
  const load=()=>apiFetch(`/claims/${claimId}/reserves`).then(setData).catch(()=>{});
  useEffect(()=>{load();},[claimId, workRefresh]);
  // WS0-D H8: the backend now refuses a write onto a VCA-maintained reserve
  // (it would be reverted by the nightly reconcile).  This used to be a
  // swallowed `catch{}` — the save silently did nothing.
  const save=async()=>{if(!form.current_balance)return;setSaving(true);setErr("");try{await apiFetch(`/claims/${claimId}/reserves`,{method:"POST",body:{...form,current_balance:parseFloat(form.current_balance)}});setShowForm(false);setForm({reserve_type:"Medical",current_balance:"",change_note:""});load();onWorkChanged&&onWorkChanged();}catch(e){let d=String(e?.message||"Save failed.");try{d=JSON.parse(e.message).detail||d;}catch{}setErr(d);}setSaving(false);};
  const close = async (r) => {
    const note = window.prompt(`Close "${r.reserve_type}" reserve?\n\nCurrent balance: ${fmt.money(r.current_balance)}\n\nThis adds a closing entry to the history and zeros out the reserve. Add an optional reason:`);
    if (note === null) return; // user cancelled the dialog
    setClosingId(r.id);
    try {
      await apiFetch(`/claims/${claimId}/reserves/${r.id}`, {
        method: "DELETE",
        body: { change_note: (note && note.trim()) || null },
      });
      load();
      onWorkChanged && onWorkChanged();
    } catch (e) {
      let d = String(e?.message || "Close failed");
      try { d = JSON.parse(e.message).detail || d; } catch {}
      setErr(d);
    } finally {
      setClosingId(null);
    }
  };
  const total=data.reserves.reduce((s,r)=>s+parseFloat(r.current_balance||0),0);
  const totalReserveSum = data.reserves.reduce((s,r)=>s+parseFloat(r.total_reserve||0),0);
  const totalPaidSum    = data.reserves.reduce((s,r)=>s+parseFloat(r.cum_paid||0),0);
  const anyVca = data.reserves.some(r=>r.vca_reserve_id!=null) || data.history.some(h=>h.vca_reserve_id!=null);
  // Phase 1 (2026-04-29): backend returns adjustment-only events with
  // total_reserve_at_event / cum_paid_at_event / remainder_at_event /
  // event_kind precomputed.  No client-side walk — numbers are exact.
  // RESERVE-HISTORY-NOISE-FILTER (2026-06-18): the backend now also strips VCA
  // "Payment of…" denorm rows + "Fix"/"FIX" balance-restatements, so the history
  // shows real adjuster decisions and reconciles to the current Total Reserve.
  const eventKindStyle = k => {
    if (k === "Initial" || k === "Create")  return { bg: "#dbeafe", color: "#1e40af" };
    if (k === "Raise")                       return { bg: "#d1fae5", color: "#065f46" };
    if (k === "Adjust" || k === "Ongoing")   return { bg: "#fef3c7", color: "#92400e" };
    if (k === "Closed")                      return { bg: "#f1f5f9", color: "#475569" };
    if (k === "Fix")                         return { bg: "#fee2e2", color: "#991b1b" };
    if (k === "Set")                         return { bg: "#ede9fe", color: "#4c1d95" };
    return { bg: "#f1f5f9", color: "#334155" };
  };
  return (<div>
    <div className="section-header"><span className="section-title">Reserves</span><button className="btn btn-blue btn-sm" onClick={()=>setShowForm(!showForm)}>{showForm?"Cancel":"+ Set Reserve"}</button></div>
    {anyVca&&<div style={{fontSize:11.5,color:"var(--text3)",margin:"-4px 0 12px",lineHeight:1.5}}>
      <strong style={{color:"var(--text2)"}}>VCA-synced reserve.</strong> The adjuster maintains <strong>Remaining</strong>; payments reduce it automatically and <strong>Total Reserve is derived</strong> (Remaining&nbsp;+&nbsp;Paid). VCA payment-snapshot, balance-restatement (&ldquo;Fix&rdquo;) rows are hidden from the adjuster history below.
    </div>}
    {err&&<div className="card" style={{marginBottom:12}}><div className="card-body" style={{color:"var(--red)",fontSize:12.5}}>{err}</div></div>}
    {showForm&&<div className="card mb-12" style={{marginBottom:12}}><div className="card-body">
      <div className="form-grid">
        <div className="form-group"><label>Type</label><select value={form.reserve_type} onChange={e=>setForm({...form,reserve_type:e.target.value})}>{RESERVE_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
        <div className="form-group"><label>Amount</label><input type="number" value={form.current_balance} onChange={e=>setForm({...form,current_balance:e.target.value})} placeholder="0.00" min="0" step="0.01" autoFocus/></div>
        <div className="form-group full"><label>Reason</label><input value={form.change_note} onChange={e=>setForm({...form,change_note:e.target.value})} placeholder="Reason for change..."/></div>
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:10}}><button className="btn btn-outline btn-sm" onClick={()=>setShowForm(false)}>Cancel</button><button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving?"Saving...":"Save Reserve"}</button></div>
    </div></div>}
    <div className="card" style={{marginBottom:12}}><div className="card-header"><h3>Current Reserves</h3></div>
      {data.reserves.length===0?<div className="empty-state">No reserves set</div>
      :<table><thead><tr>
        <th>Type</th>
        <th className="text-right">Total Reserve</th>
        <th className="text-right">Paid</th>
        <th className="text-right">Remaining</th>
        <th>Updated</th>
        <th className="text-center" style={{width:60}}>Actions</th>
      </tr></thead>
      <tbody>
        {data.reserves.map(r=><tr key={r.id}>
          <td style={{fontWeight:500}}>{r.reserve_type}
            {r.vca_reserve_id!=null&&<span className="badge" style={{marginLeft:6,background:"#f1f5f9",color:"#475569",fontSize:9.5,padding:"1px 5px"}}>VCA</span>}
          </td>
          <td className="text-right mono" style={{fontWeight:700,color:"var(--navy)"}}>{fmt.money(r.total_reserve)}</td>
          <td className="text-right mono" style={{color:"var(--text3)"}}>{fmt.money(r.cum_paid)}</td>
          <td className="text-right mono" style={{fontWeight:600}}>{fmt.money(r.current_balance)}</td>
          <td style={{fontSize:11,color:"var(--text3)"}}>{fmt.date(r.updated_at)}</td>
          <td className="text-center">
            <button className="btn btn-outline btn-sm" onClick={()=>close(r)}
              disabled={closingId===r.id || r.vca_reserve_id!=null}
              title={r.vca_reserve_id!=null
                ? "Maintained by the VCA sync — close it in VCA (a close made here would be undone by tonight's reconcile)."
                : "Close this reserve"}
              style={{padding:"2px 8px",fontSize:11,color:"#991b1b",borderColor:"#fecaca"}}>
              {closingId===r.id ? "…" : "Close"}
            </button>
          </td>
        </tr>)}
        <tr style={{background:"var(--bg3)"}}>
          <td style={{fontWeight:700,fontSize:13}}>TOTAL</td>
          <td className="text-right mono" style={{fontWeight:700,fontSize:13,color:"var(--navy)"}}>{fmt.money(totalReserveSum)}</td>
          <td className="text-right mono" style={{fontWeight:700,fontSize:13,color:"var(--text3)"}}>{fmt.money(totalPaidSum)}</td>
          <td className="text-right mono" style={{fontWeight:700,fontSize:13}}>{fmt.money(total)}</td>
          <td/><td/>
        </tr>
      </tbody></table>}
    </div>
    <div className="card"><div className="card-header"><h3>Reserve History (Adjustments)</h3>
      <div style={{fontSize:11,color:"var(--text3)",fontWeight:400,marginTop:2}}>Adjuster decisions only. Payment activity is in the Payments tab.</div>
    </div>
      {data.history.length===0?<div className="empty-state">No reserve adjustments yet</div>
      :<table><thead><tr>
        <th>Date</th>
        <th>Type</th>
        <th>Event</th>
        <th className="text-right">Total Reserve</th>
        <th className="text-right">Paid</th>
        <th className="text-right">Remainder</th>
        <th>Note</th>
      </tr></thead>
      <tbody>{data.history.map(h=>{
        const ek = eventKindStyle(h.event_kind);
        return (<tr key={h.id}>
          <td className="mono" style={{fontSize:11,color:"var(--text3)",whiteSpace:"nowrap"}}>{fmt.date(h.change_date)}</td>
          <td style={{fontSize:11}}>{h.reserve_type}</td>
          <td><span className="badge" style={{background:ek.bg,color:ek.color,fontSize:10,padding:"1px 6px"}}>{h.event_kind}</span></td>
          <td className="text-right mono" style={{fontWeight:700,color:"var(--navy)"}}>{fmt.money(h.total_reserve_at_event)}</td>
          <td className="text-right mono" style={{color:"var(--text3)"}}>{fmt.money(h.cum_paid_at_event)}</td>
          <td className="text-right mono" style={{fontWeight:600}}>{fmt.money(h.remainder_at_event)}</td>
          <td style={{fontSize:11,color:"var(--text2)",maxWidth:280,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={h.change_note}>{(h.change_note||"").replace(/^CHANGE:\s*/i,"")}</td>
        </tr>);
      })}</tbody></table>}
    </div>
  </div>);
}

export function PaymentsTab({ claimId, claim, workRefresh, onWorkChanged }) {
  // Read-only view over the structured payments table (44k+ rows synced
  // from VCA Payments).  Each row carries the running trio:
  //   Cum Paid             — running per-reserve sum up to and including this row
  //   Total Reserve        — Mark's Total Reserve estimate at the time of payment
  //                          (= Total Reserve at the most recent adjustment
  //                          event ≤ payment_date for this reserve)
  //   Remainder            — Total Reserve − Cum Paid
  // Backend handles all arithmetic; this component is pure render.
  const [data, setData] = useState({ payments: [] });
  const [dpModal, setDpModal] = useState(null);   // null | {} (new) | {payment} (edit)
  const canDirect = canOnClaim("edit_claim_financial", claim, getUser());
  const load = () => apiFetch(`/claims/${claimId}/payments`).then(setData).catch(() => setData({payments:[]}));
  useEffect(() => { load(); }, [claimId, workRefresh]);
  const totalAmount = data.payments.reduce((s,p)=>s+parseFloat(p.amount||0),0);
  // PAYMENT-CODING-1: claim-level coding warning (uncoded / needs-review / missing funding).
  const uncodedN = data.payments.filter(p => p.coding_status === "uncoded").length;
  const reviewN  = data.payments.filter(p => p.coding_status === "needs_review").length;
  const noFundN  = data.payments.filter(p => !p.funding_source).length;
  const warn = [
    uncodedN && `${uncodedN} uncoded`,
    reviewN  && `${reviewN} need review`,
    noFundN  && `${noFundN} missing funding`,
  ].filter(Boolean).join(" · ");
  return (<div>
    <div className="section-header">
      <span className="section-title">Payments</span>
      <span style={{display:"flex",alignItems:"center",gap:10}}>
        {data.payments.length > 0 && (
          <span style={{fontSize:11,color:"var(--text3)"}}>
            {data.payments.length} payment{data.payments.length===1?"":"s"} totaling {fmt.money(totalAmount)}
          </span>
        )}
        {canDirect && <button className="btn btn-blue btn-sm" onClick={()=>setDpModal({})}>+ Record direct payment</button>}
      </span>
    </div>
    {/* CLAIMS-WORKSPACE-FINISH-1: only show the coding CTA to users who can code
        payments — an adjuster can't reach Operations → Payment Coding. */}
    {warn && can("code_payments") && (
      <div style={{display:"flex",alignItems:"center",gap:8,background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:6,padding:"6px 12px",marginBottom:10,fontSize:12,color:"#92400e"}}>
        <span>⚠</span><span>Payment coding: <strong>{warn}</strong>. Code these in Operations → Payment Coding.</span>
      </div>
    )}
    <div className="card">
      <div className="card-header">
        <h3>Payment History</h3>
        <div style={{fontSize:11,color:"var(--text3)",fontWeight:400,marginTop:2}}>
          Each row shows cumulative paid, Mark&rsquo;s Total Reserve estimate at the time of payment, and the resulting Remainder.
        </div>
      </div>
      {data.payments.length === 0
        ? <div className="empty-state">No payments on this claim</div>
        : <><table>
            <thead><tr>
              <th>Date</th>
              <th>Payee</th>
              <th>Check #</th>
              <th>Funding</th>
              <th className="text-right">Amount</th>
              <th className="text-right">Cum Paid</th>
              <th className="text-right">Total Reserve</th>
              <th className="text-right">Remainder</th>
              <th>Note</th>
              <th></th>
            </tr></thead>
            <tbody>
              {data.payments.map(p => (<tr key={p.id}>
                <td className="mono" style={{fontSize:11,whiteSpace:"nowrap",color:"var(--text3)"}}>{fmt.date(p.payment_date)}</td>
                <td style={{fontSize:11,fontWeight:500,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={p.payee}>
                  {p.payee || "—"}
                  {p.entry_source === "direct_adjuster" && <StatusPill tone="blue" sm style={{marginLeft:6}} title={`Adjuster-entered direct payment${p.direct_payer_kind?` · payer: ${p.direct_payer_kind}`:""}`}>Direct</StatusPill>}
                </td>
                <td className="mono" style={{fontSize:11,whiteSpace:"nowrap",color:"var(--text2)"}}>{p.cheque_number_raw || "—"}</td>
                <td style={{fontSize:11,color:"var(--text2)",textTransform:"capitalize"}}>{p.funding_source || "—"}</td>
                <td className="text-right mono" style={{fontWeight:600}}>{fmt.money(p.amount)}</td>
                <td className="text-right mono" style={{color:"var(--text3)"}}>{fmt.money(p.cum_paid_after)}</td>
                <td className="text-right mono" style={{fontWeight:700,color:"var(--navy)"}}>{fmt.money(p.total_reserve_at_time)}</td>
                <td className="text-right mono" style={{fontWeight:600,color:p.remainder_at_time<0?"#991b1b":"inherit"}}>{fmt.money(p.remainder_at_time)}</td>
                <td style={{fontSize:11,color:"var(--text2)",maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={p.note}>{p.note || ""}</td>
                <td style={{whiteSpace:"nowrap",textAlign:"right"}}>
                  {canDirect && p.entry_source === "direct_adjuster" && (<>
                    <button title="Edit" style={{background:"transparent",border:"none",cursor:"pointer",fontSize:12,padding:"2px 4px",color:"var(--text3)"}} onClick={()=>setDpModal({payment:p})}>✎</button>
                    <button title="Delete" style={{background:"transparent",border:"none",cursor:"pointer",fontSize:12,padding:"2px 4px",color:"#dc2626"}} onClick={()=>{if(window.confirm("Delete this direct payment?")){apiFetch(`/claims/${claimId}/direct-payments/${p.id}`,{method:"DELETE"}).then(()=>{load();onWorkChanged&&onWorkChanged();}).catch(e=>alert(e.message||"Delete failed."));}}}>🗑</button>
                  </>)}
                </td>
              </tr>))}
            </tbody>
          </table></>}
    </div>
    {dpModal && <DirectPaymentModal claimId={claimId} payment={dpModal.payment}
                  onClose={()=>setDpModal(null)} onDone={()=>{setDpModal(null);load();onWorkChanged&&onWorkChanged();}} />}
  </div>);
}

const DP_PAYER_KINDS = [["","—"],["underwriter","Underwriter"],["insured","Insured/Assured"],["walters","Walters"],["other","Other"]];
const DP_PAYEE_KINDS = [["","—"],["insured","Insured/Assured"],["claimant","Claimant"],["underwriter","Underwriter"],["vendor","Vendor"],["walters","Walters"],["other","Other"]];
const DP_METHODS = ["Check","ACH","Zelle","Wire","EFT","Other"];

function DirectPaymentModal({ claimId, payment, onClose, onDone }) {
  const editing = !!payment;
  const [f, setF] = useState({
    amount: payment?.amount ?? "", payment_date: payment?.payment_date ? String(payment.payment_date).slice(0,10) : "",
    payment_method: payment?.payment_method || "", payee: payment?.payee || "",
    payee_company_id: payment?.payee_company_id || null, payee_contact_id: payment?.payee_contact_id || null,
    direct_payer_kind: payment?.direct_payer_kind || "", direct_payee_kind: payment?.direct_payee_kind || "", note: payment?.note || "",
    cheque_number_raw: payment?.cheque_number_raw || "", date_of_service: payment?.date_of_service ? String(payment.date_of_service).slice(0,10) : "",
    receipt_url: payment?.receipt_url || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [pq, setPq] = useState(""); const [phits, setPhits] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (!pq.trim()) { setPhits([]); return; }
      apiFetch(`/payees/search?q=${encodeURIComponent(pq)}&limit=8`).then(d=>setPhits(d.results||[])).catch(()=>setPhits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [pq]);
  const inp = {width:"100%",padding:"7px 9px",fontSize:13,border:"1px solid var(--border)",borderRadius:5,boxSizing:"border-box"};
  const save = () => {
    if (f.amount === "" || isNaN(Number(f.amount))) { setErr("A numeric amount is required."); return; }
    setBusy(true); setErr(null);
    const body = {...f};
    Object.keys(body).forEach(k => { if (body[k] === "" ) delete body[k]; });
    const url = editing ? `/claims/${claimId}/direct-payments/${payment.id}` : `/claims/${claimId}/direct-payments`;
    apiFetch(url, { method: editing ? "PUT" : "POST", body }).then(onDone).catch(e=>{setErr(e.message||"Save failed.");setBusy(false);});
  };
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.45)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:1000,padding:"6vh 16px"}}>
      <div onClick={e=>e.stopPropagation()} className="card" style={{background:"white",width:540,maxWidth:"100%",maxHeight:"86vh",overflowY:"auto",padding:20,borderRadius:8}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <h3 style={{margin:0,fontSize:16,color:"var(--navy)"}}>{editing ? "Edit direct payment" : "Record direct payment"}</h3>
          <button className="btn btn-sm btn-outline" onClick={onClose}>✕</button>
        </div>
        <div style={{fontSize:11,color:"var(--text3)",marginBottom:12}}>Atticus-only. Counts immediately; not synced to QBO.</div>
        {err && <div style={{background:"#FEF2F2",color:"#B91C1C",border:"1px solid #FCA5A5",padding:"8px 10px",borderRadius:5,fontSize:13,marginBottom:12}}>{err}</div>}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <label style={{fontSize:12,fontWeight:600,color:"var(--text2)"}}>Amount *<input style={inp} type="number" step="0.01" value={f.amount} onChange={e=>setF({...f,amount:e.target.value})} /></label>
          <label style={{fontSize:12,fontWeight:600,color:"var(--text2)"}}>Payment date<input style={inp} type="date" value={f.payment_date} onChange={e=>setF({...f,payment_date:e.target.value})} /></label>
          <label style={{fontSize:12,fontWeight:600,color:"var(--text2)"}}>Method<select style={inp} value={f.payment_method} onChange={e=>setF({...f,payment_method:e.target.value})}><option value="">—</option>{DP_METHODS.map(m=><option key={m} value={m}>{m}</option>)}</select></label>
          <label style={{fontSize:12,fontWeight:600,color:"var(--text2)"}}>Paid by<select style={inp} value={f.direct_payer_kind} onChange={e=>setF({...f,direct_payer_kind:e.target.value})}>{DP_PAYER_KINDS.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select></label>
          <label style={{fontSize:12,fontWeight:600,color:"var(--text2)"}}>Paid to<select style={inp} value={f.direct_payee_kind} onChange={e=>setF({...f,direct_payee_kind:e.target.value})}>{DP_PAYEE_KINDS.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select></label>
        </div>
        {f.direct_payer_kind==="underwriter" && f.direct_payee_kind==="insured" && (
          <div style={{marginTop:10,background:"#EFF6FF",border:"1px solid #BFDBFE",color:"#1E40AF",padding:"7px 10px",borderRadius:5,fontSize:12}}>
            Recorded as a <b>reimbursement received</b> — it reduces <b>Net Due to Insured</b> in the Payment History (not counted as a claim disbursement).
          </div>
        )}
        <div style={{marginTop:12,position:"relative"}}>
          <label style={{fontSize:12,fontWeight:600,color:"var(--text2)"}}>Payee
            <input style={inp} value={f.payee} onChange={e=>{setF({...f,payee:e.target.value,payee_company_id:null,payee_contact_id:null});setPq(e.target.value);}} placeholder="Type a payee name…" />
          </label>
          {phits.length>0 && (
            <div style={{position:"absolute",zIndex:5,background:"white",border:"1px solid var(--border)",borderRadius:5,width:"100%",maxHeight:180,overflowY:"auto",boxShadow:"0 6px 18px rgba(0,0,0,0.15)"}}>
              {phits.map(h=>(
                <div key={`${h.kind}-${h.entity_id}`} onClick={()=>{setF({...f,payee:h.name,payee_company_id:h.kind==="company"?h.entity_id:null,payee_contact_id:h.kind==="contact"?h.entity_id:null});setPhits([]);setPq("");}} style={{padding:"7px 10px",cursor:"pointer",fontSize:13,borderBottom:"1px solid var(--border-light,#eef1f4)"}}>
                  {h.name} <span style={{fontSize:10,color:"var(--text3)"}}>({h.kind})</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <label style={{fontSize:12,fontWeight:600,color:"var(--text2)",display:"block",marginTop:12}}>Note<textarea style={{...inp,minHeight:48}} value={f.note} onChange={e=>setF({...f,note:e.target.value})} /></label>
        <label style={{fontSize:12,fontWeight:600,color:"var(--text2)",display:"block",marginTop:12}}>Receipt / attachment URL<input style={inp} value={f.receipt_url} onChange={e=>setF({...f,receipt_url:e.target.value})} placeholder="SharePoint / document link (optional)" /></label>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:16}}>
          <button className="btn btn-sm btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-sm btn-blue" onClick={save} disabled={busy}>{editing ? "Save" : "Record payment"}</button>
        </div>
      </div>
    </div>
  );
}

export function TasksTab({ claimId, openTask, claimLabel, workRefresh, onWorkChanged, focusTaskId }) {
  // OPS-1 (2026-05-29): create/edit go through the shared TaskModal (defaults
  // assignee to the current user, has an assignee picker). Reloads on
  // workRefresh so a modal save/delete reflects here. Edit + delete on every
  // row, open and completed.
  const [tasks,setTasks]=useState([]);
  const load=()=>apiFetch(`/claims/${claimId}/tasks`).then(setTasks).catch(()=>{});
  useEffect(()=>{load();},[claimId, workRefresh]);
  const complete=async id=>{try{await apiFetch(`/tasks/${id}`,{method:"PATCH",body:{status:"Done"}});onWorkChanged&&onWorkChanged();}catch{}load();};
  const del=async id=>{if(!window.confirm("Delete this task? This cannot be undone."))return;try{await apiFetch(`/tasks/${id}`,{method:"DELETE"});load();onWorkChanged&&onWorkChanged();}catch(e){alert(e.message||"Delete failed.");}};
  const edit=t=>openTask&&openTask({task:{...t,claim_number:claimLabel}});
  // BUG-11: deep-link from My Day — highlight the row and open the task once.
  const focusDone=useRef(false);
  useEffect(()=>{
    if(!focusTaskId||focusDone.current)return;
    const t=tasks.find(x=>String(x.id)===String(focusTaskId));
    if(!t)return;
    focusDone.current=true;
    const el=document.getElementById(`task-row-${t.id}`);
    if(el)el.scrollIntoView({block:"center"});
    edit(t);
  },[tasks,focusTaskId]);
  const focusStyle=t=>String(t.id)===String(focusTaskId)?{background:"#fef9c3"}:undefined;
  const iconBtn={background:"transparent",border:"none",cursor:"pointer",fontSize:13,padding:"2px 5px",color:"var(--text3)"};
  const open=tasks.filter(t=>t.status==="Open"||t.status==="In Progress");
  const done=tasks.filter(t=>t.status==="Done");
  const Actions=({t})=>(<td style={{whiteSpace:"nowrap",textAlign:"right"}}>
    {t.status!=="Done"&&<button title="Mark done" style={{...iconBtn,color:"var(--green)"}} onClick={()=>complete(t.id)}>✓</button>}
    {openTask&&<button title="Edit" style={iconBtn} onClick={()=>edit(t)}>✎</button>}
    <button title="Delete" style={{...iconBtn,color:"#dc2626"}} onClick={()=>del(t.id)}>🗑</button>
  </td>);
  return (<div>
    <div className="section-header"><span className="section-title">Tasks ({open.length} open)</span>
      {openTask&&<button className="btn btn-blue btn-sm" onClick={()=>openTask({claimId, claimLabel})}>+ Add Task</button>}
    </div>
    <div className="card">
      {tasks.length===0?<div className="empty-state">No tasks yet</div>
      :<table><thead><tr><th>Task</th><th>Priority</th><th>Due Date</th><th>Assignee</th><th>Status</th><th></th></tr></thead>
      <tbody>
        {open.map(t=>{const overdue=t.due_date&&new Date(t.due_date)<new Date();return(<tr key={t.id} id={`task-row-${t.id}`} style={focusStyle(t)}>
          <td><div style={{fontWeight:500}}>{t.title}</div>{t.description&&<div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{t.description}</div>}</td>
          <td><span className="badge" style={{background:["High","Critical"].includes(t.priority)?"#fef3c7":"#f1f5f9",color:["High","Critical"].includes(t.priority)?"#92400e":"#334155"}}>{t.priority}</span></td>
          <td style={{color:overdue?"var(--red)":"inherit",fontWeight:overdue?600:400}}>{fmt.date(t.due_date)}{overdue&&" ⚠"}</td>
          <td style={{fontSize:12,color:"var(--text2)"}}>{t.assigned_to_name||"—"}</td>
          <td><span className="badge" style={{background:"#dbeafe",color:"#1e40af"}}>{t.status}</span></td>
          <Actions t={t}/>
        </tr>);})}
        {done.slice(0,8).map(t=>(<tr key={t.id} id={`task-row-${t.id}`} style={{opacity:0.5,...focusStyle(t)}}>
          <td style={{textDecoration:"line-through"}}>{t.title}</td>
          <td>{t.priority}</td>
          <td>{fmt.date(t.due_date)}</td>
          <td style={{fontSize:12,color:"var(--text2)"}}>{t.assigned_to_name||"—"}</td>
          <td><span className="badge" style={{background:"#d1fae5",color:"#065f46"}}>Done</span></td>
          <Actions t={t}/>
        </tr>))}
      </tbody></table>}
    </div>
  </div>);
}

export function InvoicingTab({ claimId }) {
  const [invoices,setInvoices]=useState([]);const [loading,setLoading]=useState(true);
  useEffect(()=>{apiFetch(`/claims/${claimId}/invoices`).then(d=>{setInvoices(d);setLoading(false);}).catch(()=>setLoading(false));},[claimId]);
  const total=invoices.reduce((s,i)=>s+parseFloat(i.total_amount||0),0);
  const paid=invoices.filter(i=>i.status==="Paid").reduce((s,i)=>s+parseFloat(i.approved_amount||0),0);
  const pending=invoices.filter(i=>["Pending Review","Sent to Audit","Audit Returned","Pending Approval"].includes(i.status)).reduce((s,i)=>s+parseFloat(i.total_amount||0),0);
  return (<div>
    <div className="section-header"><span className="section-title">Invoices</span></div>
    {invoices.length>0&&<div style={{display:"flex",gap:12,marginBottom:12}}>
      {[{label:"Total Invoiced",value:total,cls:""},{label:"Total Paid",value:paid,cls:"green"},{label:"Pending",value:pending,cls:"yellow"},{label:"Outstanding",value:total-paid,cls:""}].map(s=>(
        <div key={s.label} className={`stat-card ${s.cls}`} style={{flex:1,padding:"10px 14px"}}><div className="stat-label">{s.label}</div><div style={{fontSize:15,fontWeight:700,fontFamily:"JetBrains Mono,monospace",marginTop:2}}>{fmt.money(s.value)}</div></div>
      ))}
    </div>}
    <div className="card table-wrap">
      {loading?<div className="loading">Loading...</div>:invoices.length===0?<div className="empty-state">No invoices yet</div>
      :<table><thead><tr><th>Invoice #</th><th>Vendor</th><th>Date</th><th className="text-right">Amount</th><th className="text-right">Approved</th><th className="text-right">Deductible</th><th>Paid From</th><th>Pay Date</th><th>Check #</th><th>Status</th></tr></thead>
      <tbody>{invoices.map(i=><tr key={i.id}>
        <td className="mono">{i.invoice_number||`#${i.id}`}</td><td>{i.vendor_name||"—"}</td><td>{fmt.date(i.invoice_date)}</td>
        <td className="text-right mono">{fmt.money(i.total_amount)}</td><td className="text-right mono">{fmt.money(i.approved_amount)}</td><td className="text-right mono">{fmt.money(i.deductible_amount)}</td>
        <td>{i.paid_from||"—"}</td><td>{fmt.date(i.payment_date)}</td><td className="mono">{i.check_number||"—"}</td>
        <td><span className="badge" style={{background:i.status==="Paid"?"#d1fae5":i.status==="Denied"?"#fee2e2":"#dbeafe",color:i.status==="Paid"?"#065f46":i.status==="Denied"?"#991b1b":"#1e40af"}}>{i.status}</span></td>
      </tr>)}</tbody></table>}
    </div>
  </div>);
}

export function ClaimantTab({ claimId, claim, vca }) {
  // Bucket A (Kuhrt 2026-04-29): prefer the native claims.insured_* columns
  // over VCA's JSON blob.  The native columns are extracted from VCA's
  // Insured key during sync, and they're normalized + queryable; the blob
  // is denormalized and ad-hoc.  Falls back to vca?.Insured_* when the
  // native column is null (legacy claims that pre-date the extraction).
  // Same pattern as Chunk 9 Phase 3 (page title → claim.insured_name).
  const insuredName    = claim?.insured_name    || vca?.Insured;
  const insuredAddress = claim?.insured_address || vca?.Insured_Address;
  const insuredCity    = claim?.insured_city    || vca?.Insured_City;
  const insuredState   = claim?.insured_state   || vca?.Insured_State;
  const insuredZip     = claim?.insured_zip     || vca?.Insured_Zip;
  const cityStateZip   = [insuredCity, insuredState, insuredZip].filter(Boolean).join(", ") || null;
  // Loss-location fields don't yet have native columns — still VCA-only.
  const lossCityStateZip=[vca?.LossLocation_City,vca?.LossLocation_State,vca?.LossLocation_Zip].filter(Boolean).join(", ")||null;
  // Canonical vocabulary (CLAUDE.md, locked 2026-05-11): these native
  // claims.insured_* columns hold the CLAIMANT (injured person), extracted
  // from VCA's `Insured` key — NOT the policy-holding "Insured" company.
  // User-facing labels say "Claimant"; the internal var names still mirror
  // the VCA source keys until the Phase B column rename lands.
  const fields=[
    ["Claimant",insuredName],
    ["Claimant Address",insuredAddress],
    ["Claimant City/State/Zip",cityStateZip],
    ["Loss Location",vca?.LossLocation],
    ["Loss Location Address",vca?.LossLocation_Address],
    ["Loss Location City/State/Zip",lossCityStateZip],
    ["Date of Loss",fmt.date(vca?.dateofloss||vca?.Date_Of_Loss)],
    ["Cause of Loss",vca?.Cause_Of_Loss],
    ["Loss Description",vca?.Loss_Description],
    ["Line of Business",vca?.Line_Of_Business],
    ["Severity",vca?.Severity],
    ["Under Litigation",vca?.Under_Litigation===true?"Yes":vca?.Under_Litigation===false?"No":null],
  ].filter(([,v])=>v);
  return (<div className="grid-2">
    <div className="card"><div className="card-header"><h3>Claimant &amp; Loss Details</h3></div><div className="card-body">
      {fields.length===0?<div style={{fontSize:12,color:"var(--text3)"}}>No claimant data on file</div>
      :<table style={{width:"100%"}}><tbody>{fields.map(([l,v])=><FieldRow key={l} label={l} value={v}/>)}</tbody></table>}
      {claim?.vca_insured_synced_at && (
        <div style={{fontSize:11,color:"var(--text3)",marginTop:10,fontStyle:"italic"}}>
          Claimant fields last synced from VCA: {fmt.datetime(claim.vca_insured_synced_at)}
        </div>
      )}
    </div></div>
    <div className="card"><div className="card-header"><h3>Claimant PHI — pending sync extension</h3></div><div className="card-body" style={{fontSize:12,color:"var(--text2)",lineHeight:1.6}}>
      <div>Claimant-specific fields (name, SSN, DOB, injury date, injury type, body parts, employer) are <strong>not yet surfaced</strong>.</div>
      <div style={{marginTop:10}}>Source endpoints not currently in extraction:</div>
      <ul style={{marginTop:4,paddingLeft:18}}>
        <li><code>Insured (dedicated)</code> — carries SSN and DOB</li>
        <li><code>Transportation</code> — carries DOB and bodily-injury fields (peril-specific)</li>
        <li><code>Claimants</code> — synced but VCA returns 0 rows; ticket open</li>
      </ul>
      <div style={{marginTop:10,color:"var(--text3)"}}>Scheduled for Phase 3 (data migration).</div>
    </div></div>
  </div>);
}

// Tab refactor (Kuhrt 2026-04-29): Time Log + Invoicing merged into a single
// "Time/Expenses" tab.  Time entries (billable WIP, hours worked) live above
// the divider; vendor invoices (outside expenses paid out of reserves) live
// below.  Each section keeps its own data fetch, summary line, and table —
// they're conceptually related (both are how money/effort accumulates against
// the claim) but operationally separate (different data shapes, different
// workflows).  Stacking them in one tab is cleaner than two tabs that each
// answer half the question.
export function TimeExpensesTab({ claimId, openTime, claimLabel, workRefresh, onWorkChanged }) {
  // OPS-1: "+ Add Time" launches the SAME global Time Entry modal (claim
  // pre-filled). On save, App bumps workRefresh; we key TimelogTab on it so
  // the time list re-mounts and reflects the new entry without a manual refresh.
  return (<div>
    {openTime && (
      <div className="section-header">
        <span className="section-title">Time &amp; Expenses</span>
        <button className="btn btn-blue btn-sm" onClick={() => openTime({ claimId, claimLabel })}>+ Add Time</button>
      </div>
    )}
    <TimelogTab key={`tl-${workRefresh || 0}`} claimId={claimId} onWorkChanged={onWorkChanged} />
    <div style={{height:20}} />
    <InvoicingTab claimId={claimId} />
  </div>);
}

// Documents/Emails tab — placeholder for future work.  Real implementation
// will require: document storage backend (S3? local /opt/waltersnw/docs?
// SharePoint integration?), email ingest pipeline (IMAP? Microsoft Graph?
// forwarded inbox?), parsing for attachment extraction, retention policy,
// and HIPAA-safe access gating.  None of those decisions are made yet — this
// stub reserves the tab slot in the UI so users see it's coming.
export function DocumentsEmailsTab({ claimId, claim }) {
  // CLAIM-FILES-1 (2026-06-24): Finder-style file manager. 2026-07-01: Documents |
  // Emails sub-tabs — Emails becomes its own master-detail surface (a live claim
  // accrues many emails, so it needs room + a preview panel, not a strip).
  const canEdit = canOnClaim("edit_claim_basic", claim, getUser()) || canOnClaim("edit_claim", claim, getUser());
  const [sub, setSub] = useState("documents");
  const [emailCount, setEmailCount] = useState(null);
  useEffect(() => {
    apiFetch(`/claims/${claimId}/emails`).then(d => setEmailCount((d || []).length)).catch(() => setEmailCount(0));
  }, [claimId]);
  const subBtn = (id, label) => (
    <button onClick={() => setSub(id)} style={{ padding: "6px 14px", border: "none", background: "transparent", cursor: "pointer",
      fontSize: 13, fontWeight: 600, borderBottom: sub === id ? "2px solid var(--navy)" : "2px solid transparent",
      color: sub === id ? "var(--navy)" : "var(--text2)" }}>{label}</button>
  );
  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14, borderBottom: "1px solid var(--border)" }}>
        {subBtn("documents", "Documents")}
        {subBtn("emails", `Emails${emailCount != null ? ` (${emailCount})` : ""}`)}
      </div>
      {sub === "documents" && <ClaimFiles claimId={claimId} canEdit={canEdit} />}
      {sub === "emails" && <ClaimEmails claimId={claimId} claim={claim} onCount={setEmailCount} />}
    </div>
  );
}

// Claim Emails master-detail: list on the left, click a row → full preview on the
// right (subject/recipients/status/attachments + the actual sent HTML body, which
// includes the real signature). Compose lives here.
export function ClaimEmails({ claimId, claim, onCount }) {
  const [emails, setEmails] = useState([]);
  const [sel, setSel] = useState(null);       // selected email id
  const [detail, setDetail] = useState(null); // fetched /emails/{id}
  const [compose, setCompose] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = () => apiFetch(`/claims/${claimId}/emails`).then(d => { setEmails(d || []); setLoading(false); if (onCount) onCount((d || []).length); }).catch(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [claimId]);
  useEffect(() => {
    if (sel == null) { setDetail(null); return; }
    setDetail(null);
    apiFetch(`/emails/${sel}`).then(setDetail).catch(() => setDetail({ error: true }));
  }, [sel]);
  const stColor = { sent: "#166534", failed: "#b91c1c", draft: "#92400e", queued: "#92400e", received: "#1e40af", exported_to_outlook: "#6b7280" };
  const fmtDt = (s) => s ? new Date(s).toLocaleString() : "";
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span className="section-title">Emails{emails.length ? ` — ${emails.length}` : ""}</span>
        <button className="btn btn-primary btn-sm" onClick={() => setCompose(true)}>✉ Compose email</button>
      </div>
      {loading ? <div className="loading">Loading…</div> : emails.length === 0 ? (
        <div className="empty-state" style={{ padding: 24, textAlign: "center", color: "var(--text3)" }}>No emails on this claim yet. Click <strong>Compose email</strong> to send one.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 360px) 1fr", gap: 16, alignItems: "start" }}>
          <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden", maxHeight: 560, overflowY: "auto" }}>
            {emails.map(e => (
              <div key={e.id} onClick={() => setSel(e.id)} style={{ padding: "9px 12px", borderTop: "1px solid var(--border)", cursor: "pointer",
                background: sel === e.id ? "var(--bg-accent,#eef4ff)" : "transparent" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 2 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: stColor[e.status] || "var(--text3)" }}>{(e.status || "").toUpperCase()}</span>
                  {e.attachment_count > 0 && <span style={{ fontSize: 10, color: "var(--text3)" }}>📎{e.attachment_count}</span>}
                  <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--text3)" }}>{(e.sent_at || e.received_at || "").slice(0, 10)}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.subject || "(no subject)"}</div>
                <div style={{ fontSize: 11, color: "var(--text3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.direction === "out" ? "To: " : "From: "}{e.to_addrs || e.from_addr || ""}</div>
              </div>
            ))}
          </div>
          <div style={{ border: "1px solid var(--border)", borderRadius: 8, minHeight: 300 }}>
            {sel == null ? (
              <div style={{ padding: 40, textAlign: "center", color: "var(--text3)" }}>Select an email to preview.</div>
            ) : !detail ? (
              <div className="loading" style={{ padding: 24 }}>Loading…</div>
            ) : detail.error ? (
              <div className="alert-error" style={{ margin: 16 }}>Could not load this email.</div>
            ) : (
              <div>
                <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{detail.subject || "(no subject)"}</div>
                  <div style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.6 }}>
                    <div><strong>From:</strong> {detail.from_addr}{detail.sending_identity && detail.sending_identity !== "self" ? ` (${detail.sending_identity})` : ""}</div>
                    <div><strong>To:</strong> {detail.to_addrs}</div>
                    {detail.cc_addrs && <div><strong>Cc:</strong> {detail.cc_addrs}</div>}
                    {detail.bcc_addrs && <div><strong>Bcc:</strong> {detail.bcc_addrs}</div>}
                    <div><strong>Sent:</strong> {fmtDt(detail.sent_at)} · {detail.sent_by_name} · <span style={{ color: stColor[detail.status] }}>{(detail.status || "").toUpperCase()}</span></div>
                    {detail.status === "failed" && detail.error_detail && <div style={{ color: "#b91c1c" }}>{detail.error_detail}</div>}
                  </div>
                  {detail.attachments && detail.attachments.length > 0 && (
                    <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {detail.attachments.map(a => (
                        <a key={a.id} href={`/api/documents/${a.id}/download`} target="_blank" rel="noreferrer" style={{ fontSize: 11, background: "var(--bg3,#f1f5f9)", padding: "3px 8px", borderRadius: 4, textDecoration: "none", color: "var(--text2)" }}>📎 {a.original_filename}</a>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ padding: 18, background: "#fff" }}>
                  <div style={{ border: "1px solid #eee", borderRadius: 6, padding: 16, background: "#fff", color: "#222" }}
                       dangerouslySetInnerHTML={{ __html: detail.body_html || "" }} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {compose && <ComposeEmailModal claimId={claimId} claim={claim} onClose={() => setCompose(false)} onSent={() => load()} />}
    </div>
  );
}

export function TimelogTab({ claimId, onWorkChanged }) {
  // OPS-1 (2026-05-29): reads the NATIVE time_entries table (/claims/{id}/time),
  // not the legacy raw-VCA JSONB endpoint — so Date/Description populate reliably
  // AND new Atticus entries (source='app') show alongside VCA-imported ones.
  // Sticky sortable header; click a row to expand → edit/delete (app rows only;
  // VCA-synced rows are read-only, owned by the nightly reconcile).
  const [rows,setRows]=useState([]);const [loading,setLoading]=useState(true);
  const [sort,setSort]=useState({key:"work_date",dir:"desc"});
  const [expanded,setExpanded]=useState(null);
  const [editId,setEditId]=useState(null);const [editForm,setEditForm]=useState({});const [saving,setSaving]=useState(false);
  const load=()=>{setLoading(true);apiFetch(`/claims/${claimId}/time`).then(d=>{setRows(d||[]);setLoading(false);}).catch(()=>setLoading(false));};
  useEffect(()=>{load();/* eslint-disable-next-line */},[claimId]);
  const num=v=>parseFloat(v||0);
  const totalHours=rows.reduce((s,t)=>s+num(t.hours),0);
  const billableHours=rows.filter(t=>t.is_billable).reduce((s,t)=>s+num(t.hours),0);
  const wipHours=rows.filter(t=>t.is_billable&&t.status!=="Billed").reduce((s,t)=>s+num(t.hours),0);
  const sorted=[...rows].sort((a,b)=>{const {key,dir}=sort;const m=dir==="asc"?1:-1;
    let av=a[key],bv=b[key];if(key==="hours"){av=num(av);bv=num(bv);}else{av=(av==null?"":av);bv=(bv==null?"":bv);}
    if(av<bv)return -1*m;if(av>bv)return 1*m;return 0;});
  const th=(key,label,align)=>(<th onClick={()=>setSort(s=>({key,dir:s.key===key&&s.dir==="asc"?"desc":"asc"}))}
      style={{position:"sticky",top:0,zIndex:2,background:"var(--navy)",color:"white",cursor:"pointer",whiteSpace:"nowrap",
              textAlign:align||"left",padding:"8px 10px",userSelect:"none"}}>
      {label}{sort.key===key?(sort.dir==="asc"?" ▲":" ▼"):""}</th>);
  const startEdit=t=>{setEditId(t.id);setEditForm({work_date:t.work_date?String(t.work_date).slice(0,10):"",hours:t.hours,rate_type:t.rate_type||"",description:t.description||"",is_billable:!!t.is_billable});};
  const saveEdit=async id=>{setSaving(true);try{await apiFetch(`/time/${id}`,{method:"PATCH",body:{...editForm,hours:parseFloat(editForm.hours)}});setEditId(null);load();onWorkChanged&&onWorkChanged();}catch(e){alert(e.message||"Save failed.");}setSaving(false);};
  const del=async id=>{if(!window.confirm("Delete this time entry? This cannot be undone."))return;try{await apiFetch(`/time/${id}`,{method:"DELETE"});load();onWorkChanged&&onWorkChanged();}catch(e){alert(e.message||"Delete failed.");}};
  // BUG-6: mirror the backend edit-lock whitelist so the UI never offers an
  // action the API will 409. Editable only while Unbilled or Pending Invoice
  // Prep, not on a fee invoice, and not VCA-synced.
  const isMutable=t=>(t.source||"app")!=="vca"&&t.fee_invoice_id==null&&["Unbilled","Pending Invoice Prep"].includes(t.status||"Unbilled");
  // H13: a billable entry carrying $0 is a defect, not a neutral value — flag it.
  const zeroBillable=t=>!!t.is_billable&&(t.source||"app")!=="vca"&&num(t.amount)===0;
  // H12: the old single tooltip said "On an invoice — locked. Remove it from the
  // invoice to change it." for Written Off rows too — advice that cannot be
  // followed, because a written-off entry is on no invoice. Say what is true.
  const lockReason=t=>(t.status==="Written Off")
    ? "Written off — not billable. An invoice-adjustment approver can restore it from Operations → Invoice Prep."
    : (t.status==="Billed")
      ? "Legacy billed entry — read-only."
      : "On an invoice — locked. Remove it from the invoice to change it.";
  // BUG-2: flag likely duplicates — same staff, date, hours AND narrative.
  // Blank narratives and VCA rows are skipped (too noisy to match on).
  const dupIds=(()=>{const g={};rows.forEach(t=>{if((t.source||"app")==="vca")return;const d=(t.description||"").trim().toLowerCase();if(!d)return;const k=`${t.user_id}|${String(t.work_date).slice(0,10)}|${num(t.hours).toFixed(2)}|${d}`;(g[k]=g[k]||[]).push(t.id);});const s=new Set();Object.values(g).forEach(ids=>{if(ids.length>1)ids.forEach(id=>s.add(id));});return s;})();
  return (<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
      <span className="section-title">Time Log — {rows.length} entries</span>
      <div style={{display:"flex",gap:20,fontSize:12}}>
        <span>Total: <strong>{totalHours.toFixed(2)} hrs</strong></span>
        <span>Billable: <strong style={{color:"var(--green)"}}>{billableHours.toFixed(2)} hrs</strong></span>
        <span>WIP: <strong style={{color:"var(--yellow,#d97706)"}}>{wipHours.toFixed(2)} hrs</strong></span>
      </div>
    </div>
    <div className="card" style={{maxHeight:520,overflow:"auto"}}>
      {loading?<div className="loading">Loading...</div>:rows.length===0?<div className="empty-state">No time entries</div>
      :<table style={{width:"100%"}}>
        <thead><tr>
          {th("work_date","Date")}{th("user_name","Staff")}{th("rate_type","Activity")}
          {th("description","Description")}{th("hours","Hours","right")}
          {/* H13 / LD-REV-010: rate and amount are now on the row. They only
              appeared in the expander before, which is how 20/20 app-authored
              entries sat at $0 unnoticed. */}
          {th("rate","Rate","right")}{th("amount","Amount","right")}
          {th("is_billable","Billable","center")}{th("status","Status","center")}
          <th style={{position:"sticky",top:0,zIndex:2,background:"var(--navy)"}}></th>
        </tr></thead>
        <tbody>{sorted.map(t=>{
          const isVca=(t.source||"app")==="vca";const open=expanded===t.id;
          const editing=editId===t.id;const locked=!isVca&&!isMutable(t);const isDup=dupIds.has(t.id);
          const stop=e=>e.stopPropagation();
          return (<Fragment key={t.id}>
            {editing?(
              // BUG-6: true inline row editing (Kuhrt's choice over a modal).
              <tr style={{background:"#fffdf5"}}>
                <td onClick={stop}><input type="date" value={editForm.work_date} onChange={e=>setEditForm({...editForm,work_date:e.target.value})} style={{width:"100%"}}/></td>
                <td style={{whiteSpace:"nowrap"}}>{(t.user_name||"—").replace(/\s*\([^)]*\)$/,"")}</td>
                <td onClick={stop}><input value={editForm.rate_type} onChange={e=>setEditForm({...editForm,rate_type:e.target.value})} style={{width:"100%"}}/></td>
                <td onClick={stop}><input value={editForm.description} onChange={e=>setEditForm({...editForm,description:e.target.value})} style={{width:"100%"}}/></td>
                <td className="text-right" onClick={stop}><input type="number" step="0.1" min="0" value={editForm.hours} onChange={e=>setEditForm({...editForm,hours:e.target.value})} style={{width:58,textAlign:"right"}}/></td>
                <td className="text-right mono" style={{color:"var(--text3)"}} title="Resolved server-side from the rate chain; re-resolves when Activity changes.">{fmt.money(t.rate)}</td>
                <td className="text-right mono" style={{color:"var(--text3)"}}>{fmt.money(t.amount)}</td>
                <td className="text-center" onClick={stop}><input type="checkbox" checked={editForm.is_billable} onChange={e=>setEditForm({...editForm,is_billable:e.target.checked})}/></td>
                <td className="text-center"><span className="badge" style={{background:"#fef3c7",color:"#92400e",fontSize:10}}>{t.status||"Unbilled"}</span></td>
                <td className="text-center" style={{whiteSpace:"nowrap"}} onClick={stop}>
                  <button title="Save" disabled={saving} onClick={()=>saveEdit(t.id)} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:14,color:"var(--green)",padding:"0 3px"}}>{saving?"…":"✓"}</button>
                  <button title="Cancel" disabled={saving} onClick={()=>setEditId(null)} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:14,color:"var(--text3)",padding:"0 3px"}}>✕</button>
                </td>
              </tr>
            ):(
              <tr onClick={()=>{setExpanded(open?null:t.id);}} style={{cursor:"pointer",background:open?"#f5f8ff":undefined}}>
                <td style={{whiteSpace:"nowrap"}}>{isDup&&<span title="Possible duplicate — same staff, date, hours and narrative as another entry" style={{color:"#d97706",marginRight:4,cursor:"help"}}>⚠</span>}{fmt.date(t.work_date)}</td>
                <td style={{whiteSpace:"nowrap"}}>{(t.user_name||"—").replace(/\s*\([^)]*\)$/,"")}</td>
                <td>{t.rate_type||"—"}</td>
                <td style={{maxWidth:300,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:11,color:"var(--text2)"}}>{t.description||"—"}</td>
                <td className="text-right mono">{num(t.hours).toFixed(2)}</td>
                <td className="text-right mono">{fmt.money(t.rate)}</td>
                <td className="text-right mono" style={zeroBillable(t)?{color:"#b45309",fontWeight:600}:undefined}
                    title={zeroBillable(t)?"Billable but bills $0 — no rate resolved when this was entered (LD-REV-010).":undefined}>
                  {zeroBillable(t)?"⚠ ":""}{fmt.money(t.amount)}</td>
                <td className="text-center">{t.is_billable&&<span style={{color:"var(--green)",fontWeight:600}}>✓</span>}</td>
                <td className="text-center"><span className="badge" style={{background:t.status==="Billed"?"#dbeafe":t.status==="Written Off"?"#e2e8f0":"#fef3c7",color:t.status==="Billed"?"#1e40af":t.status==="Written Off"?"#475569":"#92400e",fontSize:10}}>{t.status||"Unbilled"}</span></td>
                <td className="text-center" style={{whiteSpace:"nowrap"}} onClick={stop}>
                  {isVca?<span title="Synced from VCA — managed by the nightly sync" style={{color:"var(--text3)",cursor:"help"}}>🔒</span>
                   :locked?<span title={lockReason(t)} style={{color:"var(--text3)",cursor:"help"}}>🔒</span>
                   :<>
                      <button title="Edit" onClick={()=>{setExpanded(null);startEdit(t);}} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:12,color:"var(--text3)",padding:"0 3px"}}>✎</button>
                      <button title="Delete" onClick={()=>del(t.id)} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:12,color:"#dc2626",padding:"0 3px"}}>🗑</button>
                    </>}
                </td>
              </tr>
            )}
            {open&&!editing&&<tr><td colSpan={10} style={{background:"#f8fafc",padding:"12px 16px"}}>
              <div style={{fontSize:13,marginBottom:8,whiteSpace:"pre-wrap"}}>{t.description||"(no description)"}</div>
              <div style={{display:"flex",gap:20,flexWrap:"wrap",fontSize:12,color:"var(--text2)"}}>
                <span>Activity: <strong>{t.rate_type||"—"}</strong></span>
                <span>Rate: <strong>{fmt.money(t.rate)}</strong></span>
                <span>Amount: <strong>{fmt.money(t.amount)}</strong></span>
                <span>Status: <strong>{t.status||"Unbilled"}</strong></span>
                {t.fee_invoice_id&&<span>Invoice: <strong>#{t.fee_invoice_id}</strong></span>}
              </div>
              {isVca&&<div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic",marginTop:8}}>Synced from VCA — managed by the nightly sync; edit in the source system.</div>}
            </td></tr>}
          </Fragment>);
        })}</tbody>
      </table>}
    </div>
  </div>);
}
